/**
 * PreTradingPhase Component
 *
 * Pre-trading ritual workflow with snap-scroll card layout.
 * Each ritual phase is a full-height card that snaps to center on scroll.
 * Loads settings from IndexedDB and manages local state for values.
 *
 * Features:
 * - Left-side progress nav with section tracking (IntersectionObserver)
 * - Click to navigate on desktop, passive indicator on mobile
 * - Fade-in reveal animations on scroll
 * - Keyboard navigation (arrow keys) between sections
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart,
  Activity,
  Target,
  Calendar,
  LineChart,
  Crosshair,
  CheckSquare,
  Zap,
  RefreshCw,
  AlertCircle,
  Check,
  ChevronDown,
  Settings,
  Archive,
} from 'lucide-react';

// 2. Internal absolute imports
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Skeleton,
  Spinner,
  Button,
  ButtonGroup,
  EmptyState,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  RadioGroup,
  Radio,
  Chip,
  useToastNotification,
} from '@enfomentis/ui';
import { preTradingSettingsService } from '@/services/PreTradingSettingsService';
import { marketSnapshotSettingsService } from '@/services/MarketSnapshotSettingsService';
import { instrumentService } from '@/services/InstrumentService';
import { sessionDraftService } from '@/services/SessionDraftService';
import { retiredDraftLineageService } from '@/services/RetiredDraftLineageService';
import { tradingSetupSettingsService } from '@/services/TradingSetupSettingsService';
import { accountRiskService } from '@/services/AccountRiskService';
import { accountService } from '@/services/AccountService';
import { db } from '@/lib/db';
import { getBrowserTimezone, getTodayInTimezone } from '@/utils/dateHelpers';
import { generateUUID } from '@/utils/uuid';
import type { SessionDraft, PreTradingConfigSnapshot } from '@/types/transient/SessionDraft';
import type { ObjectiveDraft } from '@/types/transient/ObjectiveDraft';
import type { EconomicEventDraft } from '@/types/transient/EconomicEventDraft';
import type { MarketStateDraft } from '@/types/transient/MarketStateDraft';
import type { PreTradingSettings, PreTradingSectionConfig, PreTradingSectionId, QueuedObjective } from '@/types/schema/PreTradingSettings';
import type { TradingObjective } from '@/types/schema/TradingObjective';
import type { TradeSetupDraft } from '@/types/transient/TradeDraft';
import type { MarketSnapshotSettings } from '@/types/schema/MarketSnapshotSettings';
import type { TradingSetupSettings } from '@/types/schema/TradingSetupSettings';
import type { TradingPhase, FlushHandler } from '@/types/shared';
import type { StrategyPlaybookPair } from '@/types/shared/TradingSetupTypes';
import type {
  TradingSetupRiskSnapshot,
  SessionStrategyOverride,
  SessionPlaybookOverride,
} from '@/types/schema/TradingSetupSnapshots';
import type { AccountRisk } from '@/types/schema/AccountRisk';
import { buildTradingSetupRiskSnapshot, computeSessionAwareRisk, determineDerivationSource } from '@/utils/riskComputationUtils';
import { isEntityActive } from '@/types/shared/EntityStatus';
import { PRE_TRADING_SECTION_IDS, PRE_TRADING_SECTION_META, normalizeFixedSections } from '@/constants/preTrading';

// 3. Relative imports
import {
  HealthMetricsInput,
  PersonalPrepFieldsInput,
  RitualProgressNav,
  HealthMetricsConfigModal,
  PersonalPrepFieldsConfigModal,
  ObjectivesSelectionStep,
  ObjectivesWorkflowConfigModal,
  MarketSnapshotsInput,
  MarketSnapshotFieldsConfigModal,
  EconomicCalendarStep,
  EconomicCalendarConfigModal,
  TradingSetupStep,
  TradingSetupConfigModal,
  FinalChecksStep,
  FinalChecksConfigModal,
  TransitionScreenModal,
  RiskDetailModal,
  type RitualSection,
  type FinalCheckEntry,
} from './PreTradingWorkflow';
import type { TradingSetupDefaultsUpdate } from '@/types/shared/TradingSetupTypes';
import { tradingObjectiveService } from '@/services/TradingObjectiveService';
import {
  buildPreTradingConfigSnapshot,
  hasHealthMetricsConfigMismatch,
  hasPersonalPrepFieldsConfigMismatch,
  hasSectionVisibilityConfigMismatch,
  hasSnapshotFieldsConfigMismatch,
  hasFinalCheckItemsConfigMismatch,
  hasTradingSetupConfigMismatch,
  computeHealthMetricsHash,
  computePersonalPrepHash,
  computeSectionVisibilityHash,
  computeMarketSnapshotFieldsHash,
  computeFinalCheckItemsHash,
  computeTradingSetupHash,
  computeRiskProfileDefaultHash,
  buildInstrumentSnapshot,
  buildFieldConfigSnapshot,
  resolveTradingSetupDefaults,
  getTradingSetupCompletionStatus,
  type SectionKey,
  type TradingSetupCompletionStatus,
  type TradingSetupCriterion,
  type TradingSetupResolutionResult,
  type TradingSetupSectionId,
} from './utils/sessionDraftUtils';

// -----------------------------------------------------------------------------
// Module-level constants and helpers
// -----------------------------------------------------------------------------

/** Debounce delay for auto-save (ms) */
const DEBOUNCE_DELAY = 400;

/** Display labels for Trading Setup completion criteria (used in blocking modal) */
const tradingSetupCriterionLabels: Record<TradingSetupCriterion, string> = {
  selections: 'No strategies/playbooks selected',
  account: 'No trading account selected',
  riskProfile: 'No risk profile selected',
  confirmation: 'Setup not confirmed',
};

/**
 * Filter healthMetrics: remove undefined and NaN values
 * Returns undefined if result is empty (to clear root key in SessionDraft)
 */
const filterHealthMetrics = (
  values: Record<string, number | undefined>
): Record<string, number> | undefined => {
  const filtered: Record<string, number> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && !Number.isNaN(value)) {
      filtered[key] = value;
    }
  }
  return Object.keys(filtered).length > 0 ? filtered : undefined;
};

/**
 * Filter personalPrep: remove undefined values (but keep false, empty strings)
 * Returns undefined if result is empty (to clear root key in SessionDraft)
 */
const filterPersonalPrep = (
  values: Record<string, boolean | string | number | undefined>
): Record<string, boolean | string | number> | undefined => {
  const filtered: Record<string, boolean | string | number> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined) {
      filtered[key] = value;
    }
  }
  return Object.keys(filtered).length > 0 ? filtered : undefined;
};

/** Data structure for Personal Preparation values */
export interface PersonalPrepData {
  healthMetrics: Record<string, number | undefined>;
  personalPrep: Record<string, boolean | string | number | undefined>;
}

export interface PreTradingPhaseProps {
  /** Function to change trading phase */
  onPhaseChange: (phase: TradingPhase) => void;
  /** Optional callback when personal prep data changes (fires on every change) */
  onDataChange?: (data: PersonalPrepData) => void;
  /** Session draft ID for auto-save functionality */
  draftId?: string | undefined;
  /** Register this phase's flush handler with TradingHub for header-level invocation */
  registerFlushHandler: (handler: FlushHandler) => void;
}

/** Scrollbar styling classes (matches onboarding pattern) */
const scrollbarClasses = `
  [&::-webkit-scrollbar]:w-2
  [&::-webkit-scrollbar-track]:bg-transparent
  [&::-webkit-scrollbar-thumb]:bg-default-300
  [&::-webkit-scrollbar-thumb]:rounded-full
  [&::-webkit-scrollbar-thumb]:border-0
  scrollbar-thin
  scrollbar-thumb-default-300
  scrollbar-track-transparent
`;

/**
 * Icon mapping from icon names to React components.
 * Maps PreTradingIconName strings to actual Lucide icon elements.
 */
const SECTION_ICON_MAP: Record<string, React.ReactNode> = {
  Heart: <Heart size={20} />,
  Activity: <Activity size={20} />,
  Target: <Target size={20} />,
  Calendar: <Calendar size={20} />,
  LineChart: <LineChart size={20} />,
  Crosshair: <Crosshair size={20} />,
  CheckSquare: <CheckSquare size={20} />,
};

/**
 * Short nav labels for each section (used in RitualProgressNav).
 * These are workflow-specific short labels for mobile/compact display.
 */
const SECTION_NAV_LABELS: Record<PreTradingSectionId, string> = {
  personalPrep: 'Prep',
  healthMetrics: 'Health',
  objectives: 'Objectives',
  economicData: 'Economic',
  preMarketAnalysis: 'Snapshot',
  tradingSetup: 'Setup',
  finalChecks: 'Checks',
};

/** Bookend section IDs (always visible, not in sectionConfig) */
type BookendId = 'welcome' | 'carry-forward-review' | 'complete';

/** Combined section ID type for workflow (bookends + configurable sections) */
type WorkflowSectionId = BookendId | PreTradingSectionId;

/** T06 (WU2E): Action type for the carry-forward per-row ButtonGroup */
type CarryForwardAction = 'bringIn' | 'notNow' | 'delete';

/** T06 (WU2E): ButtonGroup options for carry-forward row actions */
const CARRY_FORWARD_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'bringIn', label: 'Bring In' },
  { value: 'notNow', label: 'Not Now' },
  { value: 'delete', label: 'Delete' },
];

/**
 * Metadata for a workflow section including bookends.
 * Combines PRE_TRADING_SECTION_META with bookend-specific entries.
 */
interface WorkflowSectionMeta {
  icon: React.ReactNode;
  title: string;
  description: string;
  navLabel: string;
  isComingSoon: boolean;
}

/**
 * Build workflow section metadata from PRE_TRADING_SECTION_META.
 * Includes bookend entries (welcome, complete) with their own metadata.
 */
const WORKFLOW_SECTION_META: Record<WorkflowSectionId, WorkflowSectionMeta> = {
  // Bookend: Welcome
  welcome: {
    icon: <Zap size={20} />,
    title: 'Pre-Trading Ritual',
    description: 'Prepare for today\'s trading session',
    navLabel: 'Welcome',
    isComingSoon: false,
  },
  // Configurable sections from PRE_TRADING_SECTION_META
  ...Object.fromEntries(
    PRE_TRADING_SECTION_IDS.map((id) => {
      const meta = PRE_TRADING_SECTION_META[id];
      return [
        id,
        {
          icon: SECTION_ICON_MAP[meta.icon] ?? <Zap size={20} />,
          title: meta.title,
          description: meta.description,
          navLabel: SECTION_NAV_LABELS[id],
          isComingSoon: meta.isComingSoon,
        },
      ];
    })
  ) as Record<PreTradingSectionId, WorkflowSectionMeta>,
  // Bookend: Carry-Forward Review
  'carry-forward-review': {
    icon: <Archive size={20} />,
    title: 'Carry-Forward Review',
    description: 'Review trade setups carried over from previous sessions',
    navLabel: 'Carry Fwd',
    isComingSoon: false,
  },
  // Bookend: Complete
  complete: {
    icon: <Zap size={20} />,
    title: 'Ready to Trade',
    description: 'Your pre-trading ritual is complete',
    navLabel: 'Ready',
    isComingSoon: false,
  },
};

/**
 * Derive workflow sections from sectionConfig with defaults for missing IDs.
 *
 * - If sectionConfig is undefined/empty, returns all sections with default visibility and order
 * - Ignores unknown IDs in config (defensive against corrupted data)
 * - Merges missing IDs from PRE_TRADING_SECTION_IDS with defaults (visible: true, order: index)
 * - Sorts by order value
 *
 * @param sectionConfig - Section configuration from draft's preTradingConfig
 * @returns Array of section configs for all known sections, sorted by order
 */
function deriveWorkflowSections(
  sectionConfig: PreTradingSectionConfig[] | undefined
): Array<{ id: PreTradingSectionId; visible: boolean; order: number }> {
  // Build defaults for all sections
  const defaults = PRE_TRADING_SECTION_IDS.map((id, index) => ({
    id,
    visible: true,
    order: index,
  }));

  // If no config or empty, return defaults
  if (!sectionConfig || sectionConfig.length === 0) {
    return defaults;
  }

  // Create a Set of valid IDs for fast lookup
  const validIds = new Set<string>(PRE_TRADING_SECTION_IDS);

  // Merge: use config values where they exist (and are valid), defaults for missing sections
  const merged = defaults.map((defaultItem) => {
    const existing = sectionConfig.find(
      (c) => c.sectionId === defaultItem.id && validIds.has(c.sectionId)
    );
    return existing
      ? { id: existing.sectionId, visible: existing.visible, order: existing.order }
      : defaultItem;
  });

  // Normalize: inject missing non-optional sections, force visibility, pin fixed sections
  return normalizeFixedSections(merged);
}

/**
 * Canonicalized snapshot of trading setup values for confirmation comparison.
 * Sorts arrays before serializing so order differences don't break equality.
 */
interface ConfirmedSnapshot {
  pairs: string;
  accountId: string | undefined;
  instrumentIds: string;
  selectedRiskProfileId: string | undefined;
  sessionStrategyOverrides: string;
  sessionPlaybookOverrides: string;
}

function canonicalizeSnapshot(
  pairs: StrategyPlaybookPair[],
  accountId: string | undefined,
  instrumentIds: string[],
  selectedRiskProfileId: string | undefined,
  sessionStrategyOverrides?: SessionStrategyOverride[],
  sessionPlaybookOverrides?: SessionPlaybookOverride[],
): ConfirmedSnapshot {
  const sortedPairs = [...pairs].sort((a, b) =>
    a.strategyId === b.strategyId
      ? a.playbookId.localeCompare(b.playbookId)
      : a.strategyId.localeCompare(b.strategyId)
  );
  const sortedIds = [...instrumentIds].sort();
  const sortedStrategyOverrides = [...(sessionStrategyOverrides ?? [])].sort((a, b) =>
    a.strategyId.localeCompare(b.strategyId)
  );
  const sortedPlaybookOverrides = [...(sessionPlaybookOverrides ?? [])].sort((a, b) =>
    a.strategyId === b.strategyId
      ? a.playbookId.localeCompare(b.playbookId)
      : a.strategyId.localeCompare(b.strategyId)
  );
  return {
    pairs: JSON.stringify(sortedPairs),
    accountId,
    instrumentIds: JSON.stringify(sortedIds),
    selectedRiskProfileId,
    sessionStrategyOverrides: JSON.stringify(sortedStrategyOverrides),
    sessionPlaybookOverrides: JSON.stringify(sortedPlaybookOverrides),
  };
}

function snapshotsMatch(a: ConfirmedSnapshot, b: ConfirmedSnapshot): boolean {
  return a.pairs === b.pairs && a.accountId === b.accountId && a.instrumentIds === b.instrumentIds && a.selectedRiskProfileId === b.selectedRiskProfileId && a.sessionStrategyOverrides === b.sessionStrategyOverrides && a.sessionPlaybookOverrides === b.sessionPlaybookOverrides;
}

/**
 * Pre-Trading Phase Component
 *
 * Features:
 * - Snap-scroll layout with full-height cards
 * - Loads PreTradingSettings from IndexedDB
 * - Renders HealthMetricsInput and PersonalPrepFieldsInput in Card wrappers
 * - Exposes data via optional onDataChange callback for parent coordination
 * - Placeholder cards for future ritual phases
 */
export const PreTradingPhase: React.FC<PreTradingPhaseProps> = ({
  onPhaseChange,
  onDataChange,
  draftId,
  registerFlushHandler,
}) => {
  // Draft config state (snapshot from draft, or built from settings for legacy drafts)
  const [draftConfig, setDraftConfig] = useState<PreTradingConfigSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Display timezone state (loaded from user settings)
  const [displayTimezone, setDisplayTimezone] = useState<string>(() => getBrowserTimezone());

  // Value states (start empty, populated from SessionDraft if available)
  const [healthMetricsValues, setHealthMetricsValues] = useState<
    Record<string, number | undefined>
  >({});
  const [personalPrepValues, setPersonalPrepValues] = useState<
    Record<string, boolean | string | number | undefined>
  >({});
  const [objectivesValues, setObjectivesValues] = useState<ObjectiveDraft[]>([]);
  const [marketStatesValues, setMarketStatesValues] = useState<MarketStateDraft[]>([]);
  const [economicEventsValues, setEconomicEventsValues] = useState<EconomicEventDraft[]>([]);
  const [finalChecksValues, setFinalChecksValues] = useState<FinalCheckEntry[]>([]);

  // Trading Setup value states (WU7C T07)
  const [activePlaybookSelections, setActivePlaybookSelections] = useState<StrategyPlaybookPair[]>([]);
  const [defaultAccountIdValue, setDefaultAccountIdValue] = useState<string | undefined>(undefined);
  const [focusInstrumentIdsValues, setFocusInstrumentIdsValues] = useState<string[]>([]);
  const [riskSnapshotValue, setRiskSnapshotValue] = useState<TradingSetupRiskSnapshot | undefined>(undefined);
  const [setupConfirmedValue, setSetupConfirmedValue] = useState<boolean>(false);
  const [setupStepLoading, setSetupStepLoading] = useState<boolean>(true);
  const [setupStepLoadError, setSetupStepLoadError] = useState<boolean>(false);

  // Risk profile selection state (T09 Phase 4B — parent owns profile lifecycle)
  const [selectedRiskProfileId, setSelectedRiskProfileId] = useState<string | undefined>(undefined);
  // Provenance: 'default' = auto-resolved from account default, 'manual' = explicitly chosen by trader
  const [selectedRiskProfileSource, setSelectedRiskProfileSource] = useState<'default' | 'manual' | undefined>(undefined);
  const [sessionStrategyOverrides, setSessionStrategyOverrides] = useState<SessionStrategyOverride[]>([]);
  const [sessionPlaybookOverrides, setSessionPlaybookOverrides] = useState<SessionPlaybookOverride[]>([]);

  // AccountRisk data loading state (T09 — parent owns fetch, child renders)
  const [accountRiskConfig, setAccountRiskConfig] = useState<AccountRisk | undefined>(undefined);
  const [accountRiskLoading, setAccountRiskLoading] = useState(false);
  const [accountRiskLoadError, setAccountRiskLoadError] = useState(false);

  // Snapshot of trading setup values at confirmation time (for revert detection)
  const confirmedSnapshotRef = useRef<ConfirmedSnapshot | null>(null);
  // Frozen risk snapshot at confirmation time (for restoration on revert)
  const confirmedRiskSnapshotRef = useRef<TradingSetupRiskSnapshot | null>(null);

  // Track current selectedRiskProfileId for use inside effects without triggering re-runs
  const profileIdRef = useRef<string | undefined>(undefined);
  profileIdRef.current = selectedRiskProfileId;

  // Track previous defaultAccountIdValue for AccountRisk loading dedup
  const prevAccountIdForRiskRef = useRef<string | undefined>(undefined);
  // Track previous defaultAccountIdValue for profile reset dedup (separate from loading dedup)
  const prevAccountIdForProfileResetRef = useRef<string | undefined>(undefined);

  // Session date state (YYYY-MM-DD format for API queries)
  const [sessionDate, setSessionDate] = useState<string>(() => getTodayInTimezone(getBrowserTimezone()));

  // Time format state (from basic settings)
  const [timeFormat, setTimeFormat] = useState<'12h' | '24h'>('12h');

  // Auto-save state
  const [hasValidDraft, setHasValidDraft] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  // Redirect state (for missing/invalid draft)
  const [isRedirecting, setIsRedirecting] = useState(false);
  const hasRedirectedRef = useRef(false);
  const navigate = useNavigate();

  // Recovery modal state
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [isSavingNewSession, setIsSavingNewSession] = useState(false);
  const { showToast } = useToastNotification();

  // Ref for showToast to avoid stale closures in effects without adding to deps
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  // Settings Changed modal state
  const [showSettingsChangedModal, setShowSettingsChangedModal] = useState(false);
  const [isUpdatingDraftConfig, setIsUpdatingDraftConfig] = useState(false);
  // Tracks which settings sections have diverged (for unified modal)
  const [divergedSections, setDivergedSections] = useState<Array<SectionKey>>([]);
  // Per-section resolution choices: 'apply' = use new settings, 'keep' = keep draft config
  const [sectionResolutions, setSectionResolutions] = useState<
    Partial<Record<SectionKey, 'apply' | 'keep'>>
  >({});

  // Health Metrics Config modal state
  const [showHealthMetricsConfigModal, setShowHealthMetricsConfigModal] = useState(false);
  const [globalSettings, setGlobalSettings] = useState<PreTradingSettings | null>(null);

  // Personal Prep Fields Config modal state
  const [showPersonalPrepConfigModal, setShowPersonalPrepConfigModal] = useState(false);

  // Objectives Config modal state
  const [showObjectivesConfigModal, setShowObjectivesConfigModal] = useState(false);

  // No Objective Selected confirmation modal state
  const [showNoObjectiveModal, setShowNoObjectiveModal] = useState(false);

  // Trading Setup incomplete blocking modal state
  const [showSetupIncompleteModal, setShowSetupIncompleteModal] = useState(false);
  const [setupCompletionStatus, setSetupCompletionStatus] = useState<TradingSetupCompletionStatus | null>(null);

  // Transition Screen modal state
  const [showTransitionScreenModal, setShowTransitionScreenModal] = useState(false);

  // Market Snapshot Fields Config modal state
  const [showMarketSnapshotFieldsConfigModal, setShowMarketSnapshotFieldsConfigModal] = useState(false);
  const [globalMarketSnapshotSettings, setGlobalMarketSnapshotSettings] = useState<MarketSnapshotSettings | null>(null);

  // Economic Calendar Config modal state
  const [showEconomicCalendarConfigModal, setShowEconomicCalendarConfigModal] = useState(false);
  const [economicCalendarSettingsKey, setEconomicCalendarSettingsKey] = useState(0);

  // Trading Setup Config modal state (T11 in-line defaults editor)
  const [showTradingSetupConfigModal, setShowTradingSetupConfigModal] = useState(false);
  const [globalTradingSetupSettings, setGlobalTradingSetupSettings] = useState<TradingSetupSettings | null>(null);

  // Trading Setup Resolution Prompt state (T11 Phase 3 — per-section resolution after defaults save)
  const [showTradingSetupResolutionPrompt, setShowTradingSetupResolutionPrompt] = useState(false);
  const [tradingSetupResolutionResult, setTradingSetupResolutionResult] = useState<TradingSetupResolutionResult | null>(null);
  const [tradingSetupResolutionNewDefaults, setTradingSetupResolutionNewDefaults] = useState<TradingSetupDefaultsUpdate | null>(null);
  const [tradingSetupResolutionChoices, setTradingSetupResolutionChoices] = useState<Partial<Record<TradingSetupSectionId, 'apply' | 'keep'>>>({});
  const [tradingSetupResolutionSummaries, setTradingSetupResolutionSummaries] = useState<Partial<Record<TradingSetupSectionId, { newDefault: string; currentSession: string }>>>({});
  const [isApplyingResolution, setIsApplyingResolution] = useState(false);

  // Final Checks Config modal state
  const [showFinalChecksConfigModal, setShowFinalChecksConfigModal] = useState(false);
  const [showRiskDetailModal, setShowRiskDetailModal] = useState(false);
  const [riskDetailContext, setRiskDetailContext] = useState<{ type: 'strategy' | 'playbook'; strategyId: string; playbookId?: string } | null>(null);

  // Carry-forward review state
  const [carryForwardCandidates, setCarryForwardCandidates] = useState<TradeSetupDraft[]>([]);
  const [carryForwardLoading, setCarryForwardLoading] = useState(true);
  const [carryForwardSubmitting, setCarryForwardSubmitting] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  /** T06 (WU2E): maps draft.id → selected carry-forward action */
  const [selectedActions, setSelectedActions] = useState<Record<string, CarryForwardAction>>({});

  // Ref to capture values when save failure detected (avoids stale closure)
  const capturedValuesRef = useRef<{
    draftId: string;
    healthMetrics: Record<string, number | undefined>;
    personalPrep: Record<string, boolean | string | number | undefined>;
    objectives: ObjectiveDraft[];
    marketStates: MarketStateDraft[];
    economicEvents: EconomicEventDraft[];
    finalChecks: FinalCheckEntry[];
    activePlaybookSelections: StrategyPlaybookPair[];
    defaultAccountId: string | undefined;
    focusInstrumentIds: string[];
    riskSnapshot: TradingSetupRiskSnapshot | undefined;
    setupConfirmed: boolean;
    selectedRiskProfileId: string | undefined;
    selectedRiskProfileSource: 'default' | 'manual' | undefined;
    sessionStrategyOverrides: SessionStrategyOverride[];
    sessionPlaybookOverrides: SessionPlaybookOverride[];
  } | null>(null);

  // Ref to store original draft context (sessionType, date) for recovery
  const originalDraftContextRef = useRef<{
    sessionType: SessionDraft['sessionType'];
    date: string;
  } | null>(null);

  // Refs for debounced auto-save
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingValuesRef = useRef<{
    draftId: string;
    healthMetrics: Record<string, number | undefined>;
    personalPrep: Record<string, boolean | string | number | undefined>;
    objectives: ObjectiveDraft[];
    marketStates: MarketStateDraft[];
    economicEvents: EconomicEventDraft[];
    finalChecks: FinalCheckEntry[];
    activePlaybookSelections: StrategyPlaybookPair[];
    defaultAccountId: string | undefined;
    focusInstrumentIds: string[];
    riskSnapshot: TradingSetupRiskSnapshot | undefined;
    setupConfirmed: boolean;
    selectedRiskProfileId: string | undefined;
    selectedRiskProfileSource: 'default' | 'manual' | undefined;
    sessionStrategyOverrides: SessionStrategyOverride[];
    sessionPlaybookOverrides: SessionPlaybookOverride[];
  } | null>(null);

  // Retry trigger for re-loading after error
  const [retryTrigger, setRetryTrigger] = useState(0);

  // Navigation state
  const [currentSectionId, setCurrentSectionId] = useState<string>('welcome');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Reveal animation state - welcome starts revealed to prevent flash
  const [revealedSections, setRevealedSections] = useState<Set<string>>(
    () => new Set(['welcome'])
  );

  // Derive workflow sections from sectionConfig (visibility and order)
  const workflowSections = useMemo(
    () => deriveWorkflowSections(draftConfig?.sectionConfig),
    [draftConfig?.sectionConfig]
  );

  // Filter to visible sections only (for rendering)
  const visibleSections = useMemo(
    () => workflowSections.filter((s) => s.visible),
    [workflowSections]
  );

  // Build nav sections: welcome + visible sections + complete (for RitualProgressNav)
  const navSections: RitualSection[] = useMemo(() => {
    const welcomeNav: RitualSection = {
      id: 'welcome',
      label: WORKFLOW_SECTION_META.welcome.navLabel,
      icon: WORKFLOW_SECTION_META.welcome.icon,
    };
    const sectionNavs: RitualSection[] = visibleSections.map((s) => ({
      id: s.id,
      label: WORKFLOW_SECTION_META[s.id].navLabel,
      icon: WORKFLOW_SECTION_META[s.id].icon,
    }));
    const carryForwardNav: RitualSection = {
      id: 'carry-forward-review',
      label: WORKFLOW_SECTION_META['carry-forward-review'].navLabel,
      icon: WORKFLOW_SECTION_META['carry-forward-review'].icon,
    };
    const completeNav: RitualSection = {
      id: 'complete',
      label: WORKFLOW_SECTION_META.complete.navLabel,
      icon: WORKFLOW_SECTION_META.complete.icon,
    };
    return [welcomeNav, ...sectionNavs, carryForwardNav, completeNav];
  }, [visibleSections]);

  // Sync revealedSections with visibleSections when visibility changes
  // This ensures newly visible sections (e.g., from SettingsChangedModal apply) render correctly
  useEffect(() => {
    setRevealedSections((prev) => {
      // Check if any visible section is missing from revealedSections
      const missingIds = visibleSections
        .map((s) => s.id)
        .filter((id) => !prev.has(id));

      // If nothing missing, return same reference (no-op)
      if (missingIds.length === 0) {
        return prev;
      }

      // Add missing sections to revealed set
      const next = new Set(prev);
      missingIds.forEach((id) => next.add(id));
      return next;
    });
  }, [visibleSections]);

  // Load carry-forward candidates whenever draftId changes
  // Per-run cancelled flag (not mountedRef) — safe for dep-keyed effects
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (draftId === undefined) {
        if (!cancelled) {
          setCarryForwardCandidates([]);
          setCarryForwardLoading(false);
        }
        return;
      }

      if (!cancelled) setCarryForwardLoading(true);

      try {
        const candidates = await sessionDraftService.getPendingCarryForwardDrafts(draftId);
        if (!cancelled) {
          setCarryForwardCandidates(candidates);
          setCarryForwardLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          showToastRef.current({
            title: 'Failed to load carry-forward drafts',
            description: err instanceof Error ? err.message : 'Unknown error',
            color: 'danger',
          });
          setCarryForwardCandidates([]);
          setCarryForwardLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [draftId]);

  // Load settings and draft data on mount or when draftId/retryTrigger changes
  // Uses effect-local cancelled flag for StrictMode safety
  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      // Reset state at effect start to prevent stale data
      setIsLoading(true);
      setError(null);
      setHasValidDraft(false);
      setIsInitialLoadComplete(false);
      setHealthMetricsValues({});
      setPersonalPrepValues({});
      setObjectivesValues([]);
      setMarketStatesValues([]);
      setEconomicEventsValues([]);
      setActivePlaybookSelections([]);
      setDefaultAccountIdValue(undefined);
      setFocusInstrumentIdsValues([]);
      setRiskSnapshotValue(undefined);
      setSetupConfirmedValue(false);
      setSelectedRiskProfileId(undefined);
      setSelectedRiskProfileSource(undefined);
      setSessionStrategyOverrides([]);
      setSessionPlaybookOverrides([]);
      setAccountRiskConfig(undefined);
      setAccountRiskLoading(false);
      setAccountRiskLoadError(false);
      confirmedSnapshotRef.current = null;
      confirmedRiskSnapshotRef.current = null;
      prevAccountIdForRiskRef.current = undefined;
      prevAccountIdForProfileResetRef.current = undefined;
      setSessionDate(getTodayInTimezone(getBrowserTimezone()));
      setDraftConfig(null);
      setGlobalSettings(null);
      // Reset modal state (important when navigating from recovery to new draft)
      setShowRecoveryModal(false);
      setIsSavingNewSession(false);
      setShowSettingsChangedModal(false);
      setIsUpdatingDraftConfig(false);

      // Check if draftId is missing/invalid - redirect to Dashboard
      const isDraftIdValid = draftId && typeof draftId === 'string' && draftId.length > 0;
      if (!isDraftIdValid) {
        if (!hasRedirectedRef.current) {
          hasRedirectedRef.current = true;
          setIsRedirecting(true);
          navigate('/dashboard', {
            replace: true,
            state: { openSessionModal: true, reason: 'session-not-found' },
          });
        }
        return;
      }

      try {
        // Load settings and user settings in parallel
        const [loadedSettings, basicSettings, marketSnapshotSettings, tradingSetupSettings] = await Promise.all([
          preTradingSettingsService.getCurrentSettings(),
          db.basic_settings.orderBy('updatedAt').last().catch(() => undefined),
          marketSnapshotSettingsService.getCurrentSettings(),
          tradingSetupSettingsService.getCurrentSettings(),
        ]);
        if (cancelled) return;

        // Set displayTimezone from user settings (fallback to browser timezone)
        const timezone = basicSettings?.displayTimezone ?? getBrowserTimezone();
        setDisplayTimezone(timezone);

        // Set timeFormat from user settings (fallback to 12h)
        const userTimeFormat = basicSettings?.timeFormat ?? '12h';
        setTimeFormat(userTimeFormat);

        // Store global settings for config modal
        if (loadedSettings) {
          setGlobalSettings(loadedSettings);
        }

        // Load draft data - redirect if not found
        try {
          const draft = await sessionDraftService.getById(draftId);
          if (cancelled) return;

          if (draft) {
            // Populate state with null-safe spread
            setHealthMetricsValues(draft.healthMetrics ?? {});
            setPersonalPrepValues(draft.personalPrep ?? {});

            // Populate objectives: use draft if available, otherwise prepopulate from queue
            if (draft.objectives && draft.objectives.length > 0) {
              // User is revisiting - use existing objectives
              setObjectivesValues(draft.objectives);
            } else if (loadedSettings?.queuedObjectives && loadedSettings.queuedObjectives.length > 0) {
              // Prepopulate from queue - need to transform QueuedObjective to ObjectiveDraft
              const maxObjectives = draft.preTradingConfig?.maxObjectives ?? loadedSettings.maxObjectives ?? 3;
              const allObjectives = await tradingObjectiveService.getAll();
              const objectiveMap = new Map(allObjectives.map((o: TradingObjective) => [o.id, o]));

              // Filter out deleted objectives, sort by isPrimary (primary first), then cap at maxObjectives
              const validQueue = loadedSettings.queuedObjectives
                .filter((q: QueuedObjective) => objectiveMap.has(q.objectiveId))
                .sort((a: QueuedObjective, b: QueuedObjective) => {
                  // Sort primary items first to respect user's primary selection
                  if (a.isPrimary && !b.isPrimary) return -1;
                  if (!a.isPrimary && b.isPrimary) return 1;
                  return 0; // Preserve original order for non-primary items
                })
                .slice(0, maxObjectives);

              // Show toast if some objectives were omitted
              if (loadedSettings.queuedObjectives.length > maxObjectives) {
                showToastRef.current({
                  title: 'Queue Capped',
                  description: `Only ${maxObjectives} objectives loaded (max per session)`,
                  color: 'warning',
                });
              }

              // Transform to ObjectiveDraft format
              const now = new Date().toISOString();
              const prepopulatedObjectives: ObjectiveDraft[] = validQueue.map(
                (q: QueuedObjective, index: number) => ({
                  id: generateUUID(),
                  createdAt: now,
                  updatedAt: now,
                  objectiveId: q.objectiveId,
                  isPrimary: index === 0, // First is primary (after sorting, this respects queue's isPrimary)
                })
              );

              setObjectivesValues(prepopulatedObjectives);
            }
            // If both are empty, objectivesValues stays as []

            // Populate marketStates: use draft if defined, otherwise prepopulate from defaults
            // Note: Check for undefined (never set), not empty array (user intentionally cleared)
            // Track effective market states for focus instrument seeding below
            let effectiveMarketStates: MarketStateDraft[] | undefined;
            if (draft.marketStates !== undefined) {
              // User is revisiting or intentionally cleared - use existing (may be empty)
              effectiveMarketStates = draft.marketStates;
              setMarketStatesValues(draft.marketStates);
            } else if (marketSnapshotSettings?.defaultSnapshots && marketSnapshotSettings.defaultSnapshots.length > 0) {
              // First time - prepopulate from defaults
              const now = new Date().toISOString();
              const seenCombinations = new Set<string>();
              const prepopulatedMarketStates: MarketStateDraft[] = [];

              for (const defaultEntry of marketSnapshotSettings.defaultSnapshots) {
                // Skip duplicates (in case defaults have duplicate instrument+timeframe)
                const key = `${defaultEntry.instrumentId}:${defaultEntry.timeframe}`;
                if (seenCombinations.has(key)) continue;
                seenCombinations.add(key);

                // Get instrument (skip if not found, tombstoned, or not active)
                const instrument = await instrumentService.get(defaultEntry.instrumentId);
                if (!instrument || instrument.deletedAt || instrument.status !== 'active') continue;

                // Build MarketStateDraft
                const marketStateDraft: MarketStateDraft = {
                  id: generateUUID(),
                  createdAt: now,
                  updatedAt: now,
                  instrumentSnapshot: buildInstrumentSnapshot(instrument),
                  timeframe: defaultEntry.timeframe,
                  fieldConfigSnapshot: buildFieldConfigSnapshot(marketSnapshotSettings.snapshotFields),
                  snapshots: [], // Empty - user fills in T04
                };
                prepopulatedMarketStates.push(marketStateDraft);
              }

              if (prepopulatedMarketStates.length > 0) {
                effectiveMarketStates = prepopulatedMarketStates;
                setMarketStatesValues(prepopulatedMarketStates);
                // Persist immediately to avoid data loss if user exits before debounce
                await sessionDraftService.updateMarketStates(draftId, prepopulatedMarketStates);
              }
            }
            // If marketStates undefined and no defaults, marketStatesValues stays as []

            // Populate economicEvents from draft (no prepopulation logic for events)
            setEconomicEventsValues(draft.economicEvents ?? []);

            // Populate finalChecks from draft
            setFinalChecksValues(draft.finalChecks ?? []);

            // Populate trading setup fields from draft (WU7C T07)
            const effectivePairs = draft.activePlaybookSelections ?? [];
            const effectiveAccountId = draft.defaultAccountId;
            setActivePlaybookSelections(effectivePairs);
            setDefaultAccountIdValue(effectiveAccountId);
            setRiskSnapshotValue(draft.riskSnapshot);
            setSelectedRiskProfileId(draft.selectedRiskProfileId);
            setSelectedRiskProfileSource(draft.selectedRiskProfileSource);
            setSetupConfirmedValue(draft.setupConfirmed ?? false);
            setSessionStrategyOverrides(draft.sessionStrategyOverrides ?? []);
            setSessionPlaybookOverrides(draft.sessionPlaybookOverrides ?? []);

            // Focus instruments: use draft value if defined, otherwise seed from Pre-Market Snapshots
            let effectiveFocusIds: string[] = [];
            if (draft.focusInstrumentIds !== undefined) {
              // Field has been persisted before (even if empty []) — use as-is
              effectiveFocusIds = draft.focusInstrumentIds;
            } else if (effectiveMarketStates && effectiveMarketStates.length > 0) {
              // One-time seed from effective market states (draft or just-prepopulated)
              const seenIds = new Set<string>();
              for (const ms of effectiveMarketStates) {
                const instId = ms.instrumentSnapshot?.instrumentId;
                if (instId) {
                  seenIds.add(instId);
                }
              }
              if (seenIds.size > 0) {
                effectiveFocusIds = Array.from(seenIds);
              }
            }
            setFocusInstrumentIdsValues(effectiveFocusIds);

            // Seed confirmation snapshot from draft when confirmed
            if (draft.setupConfirmed) {
              confirmedSnapshotRef.current = canonicalizeSnapshot(
                effectivePairs,
                effectiveAccountId,
                effectiveFocusIds,
                draft.selectedRiskProfileId,
                draft.sessionStrategyOverrides,
                draft.sessionPlaybookOverrides,
              );
              confirmedRiskSnapshotRef.current = draft.riskSnapshot ?? null;
            }

            setHasValidDraft(true);

            // Set draft config for rendering (legacy fallback if missing)
            let configSnapshot: PreTradingConfigSnapshot | null = null;
            if (draft.preTradingConfig) {
              // Draft has config snapshot - use it directly
              configSnapshot = draft.preTradingConfig;
              setDraftConfig(configSnapshot);
            } else if (loadedSettings) {
              // Legacy draft without config - persist snapshot before rendering
              configSnapshot = buildPreTradingConfigSnapshot(loadedSettings);
              await sessionDraftService.updatePreTradingConfig(draftId, loadedSettings);
              if (cancelled) return;
              setDraftConfig(configSnapshot);
            }
            // If both missing, draftConfig stays null (empty states will render)

            // Normalize sectionConfig if missing or incomplete (after preTradingConfig exists)
            // This ensures legacy drafts and partial configs get all section IDs with proper order
            if (configSnapshot) {
              const derivedSections = deriveWorkflowSections(configSnapshot.sectionConfig);
              const currentSectionIds = new Set(
                (configSnapshot.sectionConfig ?? []).map((s) => s.sectionId)
              );
              const hasAllSections = PRE_TRADING_SECTION_IDS.every((id) =>
                currentSectionIds.has(id)
              );

              // Persist normalized config if sectionConfig was missing or incomplete
              if (!configSnapshot.sectionConfig || !hasAllSections) {
                const normalizedConfig: PreTradingSectionConfig[] = derivedSections.map((s) => ({
                  sectionId: s.id,
                  visible: s.visible,
                  order: s.order,
                }));
                await sessionDraftService.updateSectionConfig(draftId, normalizedConfig);
                if (cancelled) return;

                // Update local state with normalized config
                const updatedSnapshot: PreTradingConfigSnapshot = {
                  ...configSnapshot,
                  sectionConfig: normalizedConfig,
                };
                setDraftConfig(updatedSnapshot);
              }
            }

            // Check for config mismatch - 4 independent section checks
            const detectedDivergences: Array<SectionKey> = [];

            // Check healthMetrics divergence
            if (configSnapshot && loadedSettings) {
              if (hasHealthMetricsConfigMismatch(configSnapshot, loadedSettings)) {
                const currentHash = computeHealthMetricsHash(loadedSettings);
                const ackHash = draft.healthMetricsAckHash;
                if (!ackHash || ackHash !== currentHash) {
                  detectedDivergences.push('healthMetrics');
                }
              }
            }

            // Check personalPrep divergence
            if (configSnapshot && loadedSettings) {
              if (hasPersonalPrepFieldsConfigMismatch(configSnapshot, loadedSettings)) {
                const currentHash = computePersonalPrepHash(loadedSettings);
                const ackHash = draft.personalPrepAckHash;
                if (!ackHash || ackHash !== currentHash) {
                  detectedDivergences.push('personalPrep');
                }
              }
            }

            // Check sectionVisibility divergence
            if (configSnapshot && loadedSettings) {
              if (hasSectionVisibilityConfigMismatch(configSnapshot, loadedSettings)) {
                const currentHash = computeSectionVisibilityHash(loadedSettings);
                const ackHash = draft.sectionVisibilityAckHash;
                if (!ackHash || ackHash !== currentHash) {
                  detectedDivergences.push('sectionVisibility');
                }
              }
            }

            // Check snapshotFields divergence (only if draft has market observations)
            // Skip for new sessions with no observations - nothing to diverge from
            const firstMarketState = draft.marketStates?.[0];
            if (firstMarketState && marketSnapshotSettings?.snapshotFields) {
              const draftFieldConfig = firstMarketState.fieldConfigSnapshot;
              if (hasSnapshotFieldsConfigMismatch(draftFieldConfig, marketSnapshotSettings.snapshotFields)) {
                const currentFieldsHash = computeMarketSnapshotFieldsHash(
                  marketSnapshotSettings.snapshotFields
                );
                const fieldsAckHash = draft.marketSnapshotFieldsAckHash;
                if (!fieldsAckHash || fieldsAckHash !== currentFieldsHash) {
                  detectedDivergences.push('snapshotFields');
                }
              }
            }

            // Check finalCheckItems divergence
            if (configSnapshot && loadedSettings) {
              if (hasFinalCheckItemsConfigMismatch(configSnapshot, loadedSettings)) {
                const currentHash = computeFinalCheckItemsHash(loadedSettings);
                const ackHash = draft.finalCheckItemsAckHash;
                if (!ackHash || ackHash !== currentHash) {
                  detectedDivergences.push('finalCheckItems');
                }
              }
            }

            // Resolve effective account ID for tradingSetup comparison/hashing.
            // Settings may store undefined for single-account users who never explicitly
            // configured a default, but the draft stores the auto-emitted account ID.
            // Without resolution, this raw-vs-effective mismatch causes false positives.
            let resolvedTradingSetupSettings = tradingSetupSettings;
            if (tradingSetupSettings.defaultAccountId === undefined) {
              const activeAccounts = await accountService.getActiveAccounts();
              if (cancelled) return;
              const singleAccount = activeAccounts.length === 1 ? activeAccounts[0] : undefined;
              if (singleAccount) {
                resolvedTradingSetupSettings = { ...tradingSetupSettings, defaultAccountId: singleAccount.id };
              }
            }

            // Check tradingSetup divergence
            if (hasTradingSetupConfigMismatch(draft, resolvedTradingSetupSettings)) {
              const currentHash = computeTradingSetupHash(resolvedTradingSetupSettings);
              const ackHash = draft.tradingSetupAckHash;
              if (!ackHash || ackHash !== currentHash) {
                detectedDivergences.push('tradingSetup');
              }
            }

            // Check riskProfileDefault divergence (only when draft follows account default)
            if (
              draft.selectedRiskProfileSource === 'default' &&
              draft.selectedRiskProfileId &&
              draft.defaultAccountId
            ) {
              const riskConfigs = await accountRiskService.getActiveByAccountId(draft.defaultAccountId);
              if (cancelled) return;
              // Resolve canonical config (oldest by createdAt)
              const canonicalConfig = riskConfigs.length > 0
                ? riskConfigs.reduce((oldest, c) => (c.createdAt < oldest.createdAt ? c : oldest))
                : undefined;

              if (canonicalConfig) {
                // Resolve effective default profile (same rule as auto-resolution effect)
                const activeProfiles = canonicalConfig.riskProfiles.filter((p) =>
                  isEntityActive(p.status)
                );
                let effectiveDefaultId: string | undefined;
                if (canonicalConfig.defaultProfileId) {
                  const defaultProfile = activeProfiles.find(
                    (p) => p.id === canonicalConfig.defaultProfileId
                  );
                  if (defaultProfile) {
                    effectiveDefaultId = defaultProfile.id;
                  }
                }
                if (!effectiveDefaultId) {
                  effectiveDefaultId = activeProfiles[0]?.id;
                }

                // Only check divergence if there is a valid effective default to compare against
                if (effectiveDefaultId && draft.selectedRiskProfileId !== effectiveDefaultId) {
                  const currentHash = computeRiskProfileDefaultHash(effectiveDefaultId);
                  const ackHash = draft.riskProfileDefaultAckHash;
                  if (!ackHash || ackHash !== currentHash) {
                    detectedDivergences.push('riskProfileDefault');
                  }
                }
              }
            }

            // Show modal if any divergences detected
            if (detectedDivergences.length > 0) {
              setDivergedSections(detectedDivergences);
              // Initialize resolution state: default all diverged sections to 'apply'
              const initialResolutions = detectedDivergences.reduce(
                (acc, section) => {
                  acc[section] = 'apply';
                  return acc;
                },
                {} as Partial<Record<SectionKey, 'apply' | 'keep'>>
              );
              setSectionResolutions(initialResolutions);
              setShowSettingsChangedModal(true);
            }

            // Store original draft context for recovery modal
            originalDraftContextRef.current = {
              sessionType: draft.sessionType,
              date: draft.date,
            };

            // Set session date for API queries
            setSessionDate(draft.date);
          } else {
            // Draft not found - redirect to Dashboard
            if (!hasRedirectedRef.current) {
              hasRedirectedRef.current = true;
              setIsRedirecting(true);
              navigate('/dashboard', {
                replace: true,
                state: { openSessionModal: true, reason: 'session-not-found' },
              });
            }
            return;
          }
        } catch (draftErr) {
          // Draft load failed - redirect to Dashboard
          console.error('Failed to load SessionDraft:', draftErr);
          if (cancelled) return;
          if (!hasRedirectedRef.current) {
            hasRedirectedRef.current = true;
            setIsRedirecting(true);
            navigate('/dashboard', {
              replace: true,
              state: { openSessionModal: true, reason: 'session-not-found' },
            });
          }
          return;
        }
      } catch (err) {
        // Settings load failed - this blocks UI
        console.error('Failed to load pre-trading settings:', err);
        if (!cancelled) {
          setError('Failed to load settings. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsInitialLoadComplete(true);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [draftId, retryTrigger, navigate]);

  /**
   * Flush handler for header-level invocation (registered with TradingHub).
   * Currently a no-op (returns resolved promise). PreTradingPhase's existing
   * flush logic (flushAndProceedToLive) remains fire-and-forget for now.
   * WU2-WU6 will unify these into a single awaited flush contract.
   */
  const flushPendingData = useCallback(async (): Promise<void> => {
    // No-op: real awaited flush logic deferred to WU2-WU6
  }, []);

  // Register this phase's flush handler with TradingHub
  useEffect(() => {
    registerFlushHandler(flushPendingData);
  }, [registerFlushHandler, flushPendingData]);

  // Effect A: Debounced auto-save - watches value changes
  useEffect(() => {
    // Guard: skip if not ready for auto-save or modal is open
    if (!isInitialLoadComplete || !hasValidDraft || !draftId || showRecoveryModal || showSettingsChangedModal || showHealthMetricsConfigModal || showPersonalPrepConfigModal || showObjectivesConfigModal || showNoObjectiveModal || showMarketSnapshotFieldsConfigModal || showEconomicCalendarConfigModal || showTradingSetupConfigModal || showTradingSetupResolutionPrompt || showFinalChecksConfigModal || showRiskDetailModal || showSetupIncompleteModal) {
      // Clear pending values when not in valid auto-save state
      pendingValuesRef.current = null;
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      return;
    }

    // Store current values + draftId in pendingValuesRef
    pendingValuesRef.current = {
      draftId,
      healthMetrics: healthMetricsValues,
      personalPrep: personalPrepValues,
      objectives: objectivesValues,
      marketStates: marketStatesValues,
      economicEvents: economicEventsValues,
      finalChecks: finalChecksValues,
      activePlaybookSelections,
      defaultAccountId: defaultAccountIdValue,
      focusInstrumentIds: focusInstrumentIdsValues,
      riskSnapshot: riskSnapshotValue,
      selectedRiskProfileId,
      selectedRiskProfileSource,
      setupConfirmed: setupConfirmedValue,
      sessionStrategyOverrides,
      sessionPlaybookOverrides,
    };

    // Clear existing timer if present
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    // Schedule new debounced save
    saveTimerRef.current = setTimeout(async () => {
      // Guard: check ref still has values (could have been cleared)
      const pending = pendingValuesRef.current;
      if (!pending) return;

      // Filter values for persistence
      const filteredHealth = filterHealthMetrics(pending.healthMetrics);
      const filteredPersonal = filterPersonalPrep(pending.personalPrep);

      // Save using stored draftId (avoids stale closure)
      let success = false;
      let isTransientError = false;
      try {
        // Save personalPrep and healthMetrics
        const personalPrepSuccess = await sessionDraftService.updatePersonalPrep(
          pending.draftId,
          filteredHealth,
          filteredPersonal
        );
        // Save objectives
        const objectivesSuccess = await sessionDraftService.updateObjectives(
          pending.draftId,
          pending.objectives
        );
        // Save marketStates
        const marketStatesSuccess = await sessionDraftService.updateMarketStates(
          pending.draftId,
          pending.marketStates
        );
        // Save economicEvents
        const economicEventsSuccess = await sessionDraftService.updateEconomicEvents(
          pending.draftId,
          pending.economicEvents
        );
        // Save finalChecks
        const finalChecksSuccess = await sessionDraftService.updateFinalChecks(
          pending.draftId,
          pending.finalChecks
        );
        // Save trading setup (convert empty focusInstrumentIds to undefined at save boundary)
        const tradingSetupSuccess = await sessionDraftService.updateTradingSetup(
          pending.draftId,
          pending.activePlaybookSelections,
          pending.defaultAccountId,
          pending.focusInstrumentIds.length > 0 ? pending.focusInstrumentIds : undefined,
          pending.riskSnapshot,
          pending.setupConfirmed,
          pending.selectedRiskProfileId,
          pending.selectedRiskProfileSource,
          pending.sessionStrategyOverrides.length > 0 ? pending.sessionStrategyOverrides : undefined,
          pending.sessionPlaybookOverrides.length > 0 ? pending.sessionPlaybookOverrides : undefined
        );
        // All must succeed
        success = personalPrepSuccess && objectivesSuccess && marketStatesSuccess && economicEventsSuccess && finalChecksSuccess && tradingSetupSuccess;
      } catch (err) {
        console.error('PreTradingPhase: Unexpected error during auto-save:', err);
        // Transient/Dexie errors - show toast, don't open recovery modal
        isTransientError = true;
        showToastRef.current({
          title: 'Auto-save failed',
          description: 'Changes will retry automatically.',
          color: 'warning',
        });
      }

      // Only show recovery modal for explicit "not found" (session deleted)
      // Don't show for transient errors - those will retry on next change
      if (!success && !isTransientError) {
        // Draft was deleted - show recovery modal
        if (!hasRedirectedRef.current) {
          // Clear timer and pending values to prevent further save attempts
          if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
          }
          pendingValuesRef.current = null;

          // Capture current values using shallow copy (for recovery modal)
          capturedValuesRef.current = {
            draftId: pending.draftId,
            healthMetrics: { ...healthMetricsValues },
            personalPrep: { ...personalPrepValues },
            objectives: [...objectivesValues],
            marketStates: [...marketStatesValues],
            economicEvents: [...economicEventsValues],
            finalChecks: [...finalChecksValues],
            activePlaybookSelections: [...activePlaybookSelections],
            defaultAccountId: defaultAccountIdValue,
            focusInstrumentIds: [...focusInstrumentIdsValues],
            riskSnapshot: riskSnapshotValue,
            setupConfirmed: setupConfirmedValue,
            selectedRiskProfileId,
            selectedRiskProfileSource,
            sessionStrategyOverrides: [...sessionStrategyOverrides],
            sessionPlaybookOverrides: [...sessionPlaybookOverrides],
          };

          // Show recovery modal instead of redirecting immediately
          setShowRecoveryModal(true);
        }
        return;
      }

      // Only clear if ref still matches what we saved (avoids wiping newer values
      // that were stored while the async save was in flight)
      if (pendingValuesRef.current === pending) {
        pendingValuesRef.current = null;
      }
    }, DEBOUNCE_DELAY);

    // Cleanup: only clear timer (does NOT flush - avoids flushing on every dep change)
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, [healthMetricsValues, personalPrepValues, objectivesValues, marketStatesValues, economicEventsValues, finalChecksValues, activePlaybookSelections, defaultAccountIdValue, focusInstrumentIdsValues, riskSnapshotValue, setupConfirmedValue, selectedRiskProfileId, selectedRiskProfileSource, sessionStrategyOverrides, sessionPlaybookOverrides, isInitialLoadComplete, hasValidDraft, draftId, showRecoveryModal, showSettingsChangedModal, showHealthMetricsConfigModal, showPersonalPrepConfigModal, showObjectivesConfigModal, showNoObjectiveModal, showMarketSnapshotFieldsConfigModal, showEconomicCalendarConfigModal, showTradingSetupConfigModal, showTradingSetupResolutionPrompt, showFinalChecksConfigModal, showRiskDetailModal, showSetupIncompleteModal]);

  // Effect B: Unmount flush - ensures pending save completes on unmount
  useEffect(() => {
    // No effect body needed - just cleanup
    return () => {
      // Clear any pending timer
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }

      // Flush pending values if any
      const pending = pendingValuesRef.current;
      if (pending) {
        const filteredHealth = filterHealthMetrics(pending.healthMetrics);
        const filteredPersonal = filterPersonalPrep(pending.personalPrep);

        // Fire-and-forget save for personalPrep/healthMetrics (component is unmounting)
        sessionDraftService.updatePersonalPrep(
          pending.draftId,
          filteredHealth,
          filteredPersonal
        );

        // Fire-and-forget save for objectives
        sessionDraftService.updateObjectives(pending.draftId, pending.objectives);

        // Fire-and-forget save for marketStates
        sessionDraftService.updateMarketStates(pending.draftId, pending.marketStates);

        // Fire-and-forget save for economicEvents
        sessionDraftService.updateEconomicEvents(pending.draftId, pending.economicEvents);

        // Fire-and-forget save for finalChecks
        sessionDraftService.updateFinalChecks(pending.draftId, pending.finalChecks);

        // Fire-and-forget save for trading setup (convert empty focusInstrumentIds to undefined)
        sessionDraftService.updateTradingSetup(
          pending.draftId,
          pending.activePlaybookSelections,
          pending.defaultAccountId,
          pending.focusInstrumentIds.length > 0 ? pending.focusInstrumentIds : undefined,
          pending.riskSnapshot,
          pending.setupConfirmed,
          pending.selectedRiskProfileId,
          pending.selectedRiskProfileSource,
          pending.sessionStrategyOverrides.length > 0 ? pending.sessionStrategyOverrides : undefined,
          pending.sessionPlaybookOverrides.length > 0 ? pending.sessionPlaybookOverrides : undefined
        );
      }
    };
  }, []); // Empty deps - runs once on mount, cleanup only on unmount

  // Effect: Load canonical AccountRisk config when selected account changes (T09)
  useEffect(() => {
    if (!isInitialLoadComplete) return; // Wait for draft load to set defaultAccountIdValue

    const accountId = defaultAccountIdValue;
    const prevAccountId = prevAccountIdForRiskRef.current;
    prevAccountIdForRiskRef.current = accountId;

    // Clear config when no account selected
    if (!accountId) {
      setAccountRiskConfig(undefined);
      setAccountRiskLoading(false);
      setAccountRiskLoadError(false);
      return;
    }

    // Skip re-fetch if account didn't actually change
    if (accountId === prevAccountId && accountRiskConfig !== undefined) return;

    let cancelled = false;
    setAccountRiskLoading(true);
    setAccountRiskLoadError(false);

    void (async () => {
      try {
        const configs = await accountRiskService.getActiveByAccountId(accountId);
        if (cancelled) return;

        if (configs.length === 0) {
          setAccountRiskConfig(undefined);
        } else {
          // Deterministic: oldest config by createdAt (matches getDefaultRiskProfile logic)
          const sorted = [...configs].sort((a, b) =>
            a.createdAt.localeCompare(b.createdAt)
          );
          setAccountRiskConfig(sorted[0]);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('PreTradingPhase: Failed to load AccountRisk config:', err);
          setAccountRiskConfig(undefined);
          setAccountRiskLoadError(true);
        }
      } finally {
        if (!cancelled) {
          setAccountRiskLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- prevAccountIdForRiskRef comparison handles dedup
  }, [defaultAccountIdValue, isInitialLoadComplete]);

  // Effect: Reset profile when selected account changes (T09 Phase 4B)
  useEffect(() => {
    if (!isInitialLoadComplete) return;

    const accountId = defaultAccountIdValue;
    const prev = prevAccountIdForProfileResetRef.current;
    prevAccountIdForProfileResetRef.current = accountId;

    // Only reset when account actually changed, not on initial mount
    if (prev !== undefined && prev !== accountId) {
      handleProfileChangeRef.current(undefined, undefined);
    }
  }, [defaultAccountIdValue, isInitialLoadComplete]);

  // Effect: Auto-resolve default profile when config loads and no valid profile is selected (T09 Phase 4B)
  useEffect(() => {
    if (!isInitialLoadComplete) return;
    if (accountRiskLoading) return;
    if (!accountRiskConfig) return;

    const activeProfiles = accountRiskConfig.riskProfiles.filter((p) =>
      isEntityActive(p.status)
    );
    if (activeProfiles.length === 0) return;

    const currentProfileId = profileIdRef.current;

    // Check if current selection is still valid
    const isValid = currentProfileId !== undefined &&
      activeProfiles.some((p) => p.id === currentProfileId);
    if (isValid) return;

    // Resolve default: config.defaultProfileId (if active) → first active profile
    let resolvedId: string | undefined;
    if (accountRiskConfig.defaultProfileId) {
      const defaultProfile = activeProfiles.find(
        (p) => p.id === accountRiskConfig.defaultProfileId
      );
      if (defaultProfile) {
        resolvedId = defaultProfile.id;
      }
    }
    if (!resolvedId) {
      resolvedId = activeProfiles[0]?.id;
    }

    if (resolvedId) {
      handleProfileChangeRef.current(resolvedId, 'default');
    }
  }, [accountRiskLoading, accountRiskConfig, isInitialLoadComplete]);

  /** Handle health metrics value changes */
  const handleHealthMetricsChange = useCallback(
    (values: Record<string, number | undefined>) => {
      setHealthMetricsValues(values);
      onDataChange?.({
        healthMetrics: values,
        personalPrep: personalPrepValues,
      });
    },
    [onDataChange, personalPrepValues]
  );

  /** Handle personal prep field value changes */
  const handlePersonalPrepChange = useCallback(
    (values: Record<string, boolean | string | number | undefined>) => {
      setPersonalPrepValues(values);
      onDataChange?.({
        healthMetrics: healthMetricsValues,
        personalPrep: values,
      });
    },
    [onDataChange, healthMetricsValues]
  );

  /** Handle objectives value changes */
  const handleObjectivesChange = useCallback(
    (objectives: ObjectiveDraft[]) => {
      setObjectivesValues(objectives);
    },
    []
  );

  /** Handle market states value changes */
  const handleMarketStatesChange = useCallback(
    (marketStates: MarketStateDraft[]) => {
      setMarketStatesValues(marketStates);
    },
    []
  );

  /** Handle economic events value changes */
  const handleEconomicEventsChange = useCallback(
    (economicEvents: EconomicEventDraft[]) => {
      setEconomicEventsValues(economicEvents);
    },
    []
  );

  /** Handle final checks value changes */
  const handleFinalChecksChange = useCallback(
    (finalChecks: FinalCheckEntry[]) => {
      setFinalChecksValues(finalChecks);
    },
    []
  );

  /** Handle profile change — sets profile + source and runs confirmation guard */
  const handleProfileChange = useCallback(
    (profileId: string | undefined, source: 'default' | 'manual' | undefined) => {
      setSelectedRiskProfileId(profileId);
      setSelectedRiskProfileSource(source);

      // Confirmation guard: compare against confirmed snapshot
      const snapshot = confirmedSnapshotRef.current;
      if (snapshot) {
        const incoming = canonicalizeSnapshot(
          activePlaybookSelections,
          defaultAccountIdValue,
          focusInstrumentIdsValues,
          profileId,
          sessionStrategyOverrides,
          sessionPlaybookOverrides,
        );
        if (snapshotsMatch(incoming, snapshot)) {
          setSetupConfirmedValue(true);
          setRiskSnapshotValue(confirmedRiskSnapshotRef.current ?? undefined);
        } else {
          setSetupConfirmedValue(false);
          setRiskSnapshotValue(undefined);
        }
      } else {
        setSetupConfirmedValue(false);
        setRiskSnapshotValue(undefined);
      }
    },
    [activePlaybookSelections, defaultAccountIdValue, focusInstrumentIdsValues, sessionStrategyOverrides, sessionPlaybookOverrides]
  );

  // Ref for handleProfileChange to allow effects to call without stale closures
  const handleProfileChangeRef = useRef(handleProfileChange);
  handleProfileChangeRef.current = handleProfileChange;

  /** Handle trading setup value changes (setup confirmation guard) */
  const handleTradingSetupChange = useCallback(
    (values: {
      activePlaybookSelections: StrategyPlaybookPair[];
      defaultAccountId: string | undefined;
      focusInstrumentIds: string[];
    }) => {
      // Detect whether any trading setup field changed
      const pairsChanged =
        JSON.stringify(values.activePlaybookSelections) !== JSON.stringify(activePlaybookSelections);
      const accountChanged = values.defaultAccountId !== defaultAccountIdValue;
      const instrumentsChanged =
        JSON.stringify(values.focusInstrumentIds) !== JSON.stringify(focusInstrumentIdsValues);

      setActivePlaybookSelections(values.activePlaybookSelections);
      setDefaultAccountIdValue(values.defaultAccountId);
      setFocusInstrumentIdsValues(values.focusInstrumentIds);

      // Setup confirmation guard: compare against confirmed snapshot
      if (pairsChanged || accountChanged || instrumentsChanged) {
        const snapshot = confirmedSnapshotRef.current;
        if (snapshot) {
          const incoming = canonicalizeSnapshot(
            values.activePlaybookSelections,
            values.defaultAccountId,
            values.focusInstrumentIds,
            selectedRiskProfileId,
            sessionStrategyOverrides,
            sessionPlaybookOverrides,
          );
          if (snapshotsMatch(incoming, snapshot)) {
            setSetupConfirmedValue(true);
            setRiskSnapshotValue(confirmedRiskSnapshotRef.current ?? undefined);
          } else {
            setSetupConfirmedValue(false);
            setRiskSnapshotValue(undefined);
          }
        } else {
          setSetupConfirmedValue(false);
          setRiskSnapshotValue(undefined);
        }
      }
    },
    [activePlaybookSelections, defaultAccountIdValue, focusInstrumentIdsValues, selectedRiskProfileId, sessionStrategyOverrides, sessionPlaybookOverrides]
  );

  /** Handle setup confirmed change — captures/clears confirmation snapshot, builds/clears risk snapshot */
  const handleSetupConfirmedChange = useCallback((confirmed: boolean) => {
    if (confirmed) {
      // Guard: cannot build valid snapshot without required inputs
      if (!defaultAccountIdValue || !selectedRiskProfileId || !accountRiskConfig) {
        return;
      }
      const resolvedProfile = accountRiskConfig.riskProfiles.find(
        (p) => p.id === selectedRiskProfileId
      );
      if (!resolvedProfile) {
        return;
      }

      // Build risk snapshot with session-aware effective risk per pair
      const sessionOverridesArg = {
        strategyOverrides: sessionStrategyOverrides,
        playbookOverrides: sessionPlaybookOverrides,
      };
      const pairsWithRisk = activePlaybookSelections.map((pair) => {
        const effectiveRisk = computeSessionAwareRisk(pair.strategyId, pair.playbookId, resolvedProfile, sessionOverridesArg);
        const derivation = determineDerivationSource(pair.strategyId, pair.playbookId, resolvedProfile, sessionOverridesArg);
        return {
          ...pair,
          effectiveRisk,
          derivedFromLevel: derivation.level,
          derivedFromStage: derivation.stage,
        };
      });
      const snapshot = buildTradingSetupRiskSnapshot({
        defaultAccountId: defaultAccountIdValue,
        pairs: pairsWithRisk,
        selectedRiskProfileId,
        selectedRiskProfileName: resolvedProfile.name,
        ...(sessionStrategyOverrides.length > 0 ? { sessionStrategyOverrides } : {}),
        ...(sessionPlaybookOverrides.length > 0 ? { sessionPlaybookOverrides } : {}),
      });
      setRiskSnapshotValue(snapshot);
      confirmedRiskSnapshotRef.current = snapshot;

      confirmedSnapshotRef.current = canonicalizeSnapshot(
        activePlaybookSelections,
        defaultAccountIdValue,
        focusInstrumentIdsValues,
        selectedRiskProfileId,
        sessionStrategyOverrides,
        sessionPlaybookOverrides,
      );
    } else {
      confirmedSnapshotRef.current = null;
      confirmedRiskSnapshotRef.current = null;
      setRiskSnapshotValue(undefined);
    }
    setSetupConfirmedValue(confirmed);
  }, [activePlaybookSelections, defaultAccountIdValue, focusInstrumentIdsValues, selectedRiskProfileId, accountRiskConfig, sessionStrategyOverrides, sessionPlaybookOverrides]);

  /** Handle retry button click */
  const handleRetry = () => {
    setRetryTrigger((prev) => prev + 1);
  };

  /** Handle "Go to Dashboard" in recovery modal */
  const handleDiscardChanges = useCallback(() => {
    // Clear captured values to prevent stale data
    capturedValuesRef.current = null;

    // Mark as redirected to prevent duplicate actions
    hasRedirectedRef.current = true;

    // Close modal and show redirecting state
    setShowRecoveryModal(false);
    setIsRedirecting(true);

    // Navigate to Dashboard with session modal open
    navigate('/dashboard', {
      replace: true,
      state: { openSessionModal: true, reason: 'session-deleted' },
    });
  }, [navigate]);

  /** Handle "Start New Session" in recovery modal */
  const handleSaveAsNewSession = useCallback(async () => {
    setIsSavingNewSession(true);

    try {
      // Get sessionType and date from original draft context, with fallbacks
      const sessionType = originalDraftContextRef.current?.sessionType ?? 'live';
      const date = originalDraftContextRef.current?.date ?? getTodayInTimezone(displayTimezone);

      // Create new draft
      const newDraft = await sessionDraftService.create(sessionType, date);

      // Try to restore captured values to new draft
      const captured = capturedValuesRef.current;
      if (captured) {
        const filteredHealth = filterHealthMetrics(captured.healthMetrics);
        const filteredPersonal = filterPersonalPrep(captured.personalPrep);

        try {
          // Restore health metrics and personal prep
          await sessionDraftService.updatePersonalPrep(
            newDraft.id,
            filteredHealth,
            filteredPersonal
          );

          // Restore objectives if any were captured
          if (captured.objectives.length > 0) {
            await sessionDraftService.updateObjectives(newDraft.id, captured.objectives);
          }

          // Restore marketStates if any were captured
          if (captured.marketStates.length > 0) {
            await sessionDraftService.updateMarketStates(newDraft.id, captured.marketStates);
          }

          // Restore economicEvents if any were captured
          if (captured.economicEvents.length > 0) {
            await sessionDraftService.updateEconomicEvents(newDraft.id, captured.economicEvents);
          }

          // Restore finalChecks if any were captured
          if (captured.finalChecks.length > 0) {
            await sessionDraftService.updateFinalChecks(newDraft.id, captured.finalChecks);
          }

          // Restore trading setup if any selections were captured
          if (captured.activePlaybookSelections.length > 0 || captured.defaultAccountId !== undefined || captured.focusInstrumentIds.length > 0) {
            await sessionDraftService.updateTradingSetup(
              newDraft.id,
              captured.activePlaybookSelections,
              captured.defaultAccountId,
              captured.focusInstrumentIds.length > 0 ? captured.focusInstrumentIds : undefined,
              captured.riskSnapshot,
              captured.setupConfirmed,
              captured.selectedRiskProfileId,
              captured.selectedRiskProfileSource,
              captured.sessionStrategyOverrides.length > 0 ? captured.sessionStrategyOverrides : undefined,
              captured.sessionPlaybookOverrides.length > 0 ? captured.sessionPlaybookOverrides : undefined
            );
          }
        } catch (updateErr) {
          // Partial failure: draft created but values couldn't be restored
          console.error('Failed to restore values to new session:', updateErr);
          showToast({
            title: 'New session created',
            description: 'Some values couldn\'t be restored.',
            color: 'warning',
          });
        }
      }

      // Clear captured values
      capturedValuesRef.current = null;

      // Close modal
      setShowRecoveryModal(false);

      // Navigate to new draft (effect will re-run and load it)
      navigate(`/trading/${newDraft.id}`, { replace: true });
    } catch (err) {
      console.error('Failed to create new session:', err);
      showToast({
        title: 'Failed to create new session',
        description: 'Please try again.',
        color: 'danger',
      });
      setIsSavingNewSession(false);
      // Keep modal open on error
    }
  }, [displayTimezone, navigate, showToast]);

  // Compute whether all diverged sections are already set to 'apply' or 'keep'
  // Used to disable/mute the "Apply all" / "Keep all" buttons when clicking would have no effect
  const allApply = divergedSections.length > 0 &&
    divergedSections.every((s) => (sectionResolutions[s] ?? 'apply') === 'apply');
  const allKept = divergedSections.length > 0 &&
    divergedSections.every((s) => sectionResolutions[s] === 'keep');

  /** Set all diverged sections to 'apply' */
  const handleApplyAll = useCallback(() => {
    // Guard: no-op if all sections already set to 'apply'
    if (divergedSections.length === 0 ||
        divergedSections.every((s) => (sectionResolutions[s] ?? 'apply') === 'apply')) {
      return;
    }

    const newResolutions = divergedSections.reduce(
      (acc, section) => {
        acc[section] = 'apply';
        return acc;
      },
      {} as Partial<Record<SectionKey, 'apply' | 'keep'>>
    );
    setSectionResolutions(newResolutions);
  }, [divergedSections, sectionResolutions]);

  /** Set all diverged sections to 'keep' */
  const handleKeepAll = useCallback(() => {
    // Guard: no-op if all sections already set to 'keep'
    if (divergedSections.length === 0 ||
        divergedSections.every((s) => sectionResolutions[s] === 'keep')) {
      return;
    }

    const newResolutions = divergedSections.reduce(
      (acc, section) => {
        acc[section] = 'keep';
        return acc;
      },
      {} as Partial<Record<SectionKey, 'apply' | 'keep'>>
    );
    setSectionResolutions(newResolutions);
  }, [divergedSections, sectionResolutions]);

  /** Handle confirm in Settings Changed modal - processes each section per its resolution */
  const handleConfirmResolution = useCallback(async () => {
    if (!draftId) return;

    setIsUpdatingDraftConfig(true);

    // Track if any section mutated draft state that needs rehydration (apply OR keep-with-mutation)
    let appliedAny = false;
    // Track if any section actually applied current settings (for success toast only)
    let anySettingsApplied = false;

    try {
      // Capture pre-resolution profile state before any section handlers mutate the draft.
      // Needed by riskProfileDefault keep branch, which must preserve the profile ID
      // even if tradingSetup apply clears it from Dexie first.
      const preResolutionDraft = await sessionDraftService.getById(draftId);
      if (!preResolutionDraft) {
        hasRedirectedRef.current = true;
        setIsRedirecting(true);
        navigate('/dashboard', {
          replace: true,
          state: { openSessionModal: true, reason: 'session-deleted' },
        });
        return;
      }
      const preResolutionProfileId = preResolutionDraft.selectedRiskProfileId;

      // Fetch fresh settings once (shared across pre-trading sections)
      const freshSettings = await preTradingSettingsService.getCurrentSettings();

      // Process healthMetrics section if diverged
      if (divergedSections.includes('healthMetrics')) {
        const resolution = sectionResolutions['healthMetrics'] ?? 'apply';

        if (resolution === 'apply') {
          if (!freshSettings) {
            showToast({
              title: 'Settings unavailable',
              description: 'Cannot update health metrics without pre-trading settings.',
              color: 'danger',
            });
            setIsUpdatingDraftConfig(false);
            return;
          }

          // Update healthMetrics config (migrates values)
          const updateSuccess = await sessionDraftService.updateHealthMetricsConfig(
            draftId,
            freshSettings.healthMetrics
          );
          if (!updateSuccess) {
            hasRedirectedRef.current = true;
            setIsRedirecting(true);
            navigate('/dashboard', {
              replace: true,
              state: { openSessionModal: true, reason: 'session-deleted' },
            });
            return;
          }
          appliedAny = true;
          anySettingsApplied = true;
        }

        // Set ack hash (both apply and keep)
        if (freshSettings) {
          const ackHash = computeHealthMetricsHash(freshSettings);
          const ackSuccess = await sessionDraftService.setHealthMetricsAckHash(draftId, ackHash);
          if (!ackSuccess) {
            hasRedirectedRef.current = true;
            setIsRedirecting(true);
            navigate('/dashboard', {
              replace: true,
              state: { openSessionModal: true, reason: 'session-deleted' },
            });
            return;
          }
        } else if (resolution === 'keep') {
          // "Keep" selected but settings unavailable - show toast and keep modal open
          showToast({
            title: 'Settings unavailable',
            description: 'Cannot acknowledge health metrics changes without settings.',
            color: 'danger',
          });
          setIsUpdatingDraftConfig(false);
          return;
        }
      }

      // Process personalPrep section if diverged
      if (divergedSections.includes('personalPrep')) {
        const resolution = sectionResolutions['personalPrep'] ?? 'apply';

        if (resolution === 'apply') {
          if (!freshSettings) {
            showToast({
              title: 'Settings unavailable',
              description: 'Cannot update personal prep without pre-trading settings.',
              color: 'danger',
            });
            setIsUpdatingDraftConfig(false);
            return;
          }

          // Update personalPrep config (migrates values)
          const updateSuccess = await sessionDraftService.updatePersonalPrepFieldsConfig(
            draftId,
            freshSettings.personalPrepFields
          );
          if (!updateSuccess) {
            hasRedirectedRef.current = true;
            setIsRedirecting(true);
            navigate('/dashboard', {
              replace: true,
              state: { openSessionModal: true, reason: 'session-deleted' },
            });
            return;
          }
          appliedAny = true;
          anySettingsApplied = true;
        }

        // Set ack hash (both apply and keep)
        if (freshSettings) {
          const ackHash = computePersonalPrepHash(freshSettings);
          const ackSuccess = await sessionDraftService.setPersonalPrepAckHash(draftId, ackHash);
          if (!ackSuccess) {
            hasRedirectedRef.current = true;
            setIsRedirecting(true);
            navigate('/dashboard', {
              replace: true,
              state: { openSessionModal: true, reason: 'session-deleted' },
            });
            return;
          }
        } else if (resolution === 'keep') {
          showToast({
            title: 'Settings unavailable',
            description: 'Cannot acknowledge personal prep changes without settings.',
            color: 'danger',
          });
          setIsUpdatingDraftConfig(false);
          return;
        }
      }

      // Process sectionVisibility section if diverged
      if (divergedSections.includes('sectionVisibility')) {
        const resolution = sectionResolutions['sectionVisibility'] ?? 'apply';

        if (resolution === 'apply') {
          if (!freshSettings?.sectionConfig) {
            showToast({
              title: 'Settings unavailable',
              description: 'Cannot update section visibility without pre-trading settings.',
              color: 'danger',
            });
            setIsUpdatingDraftConfig(false);
            return;
          }

          // Normalize sectionConfig to PreTradingSectionConfig format
          const normalizedConfig = freshSettings.sectionConfig.map((s) => ({
            sectionId: s.sectionId,
            visible: s.visible,
            order: s.order,
          }));

          // Update sectionConfig
          const updateSuccess = await sessionDraftService.updateSectionConfig(
            draftId,
            normalizedConfig
          );
          if (!updateSuccess) {
            hasRedirectedRef.current = true;
            setIsRedirecting(true);
            navigate('/dashboard', {
              replace: true,
              state: { openSessionModal: true, reason: 'session-deleted' },
            });
            return;
          }
          appliedAny = true;
          anySettingsApplied = true;
        }

        // Set ack hash (both apply and keep)
        if (freshSettings) {
          const ackHash = computeSectionVisibilityHash(freshSettings);
          const ackSuccess = await sessionDraftService.setSectionVisibilityAckHash(draftId, ackHash);
          if (!ackSuccess) {
            hasRedirectedRef.current = true;
            setIsRedirecting(true);
            navigate('/dashboard', {
              replace: true,
              state: { openSessionModal: true, reason: 'session-deleted' },
            });
            return;
          }
        } else if (resolution === 'keep') {
          showToast({
            title: 'Settings unavailable',
            description: 'Cannot acknowledge section visibility changes without settings.',
            color: 'danger',
          });
          setIsUpdatingDraftConfig(false);
          return;
        }
      }

      // Process snapshotFields section if diverged
      if (divergedSections.includes('snapshotFields')) {
        const resolution = sectionResolutions['snapshotFields'] ?? 'apply';
        const freshSnapshotSettings = await marketSnapshotSettingsService.getCurrentSettings();

        if (resolution === 'apply') {
          if (!freshSnapshotSettings?.snapshotFields?.length) {
            showToast({
              title: 'Settings unavailable',
              description: 'Cannot update draft without snapshot field settings.',
              color: 'danger',
            });
            setIsUpdatingDraftConfig(false);
            return;
          }

          // Build new field config snapshot
          const newFieldConfig = buildFieldConfigSnapshot(freshSnapshotSettings.snapshotFields);

          // Update field config on all market states in DB
          const updateSuccess = await sessionDraftService.updateMarketStatesFieldConfig(
            draftId,
            newFieldConfig
          );
          if (!updateSuccess) {
            hasRedirectedRef.current = true;
            setIsRedirecting(true);
            navigate('/dashboard', {
              replace: true,
              state: { openSessionModal: true, reason: 'session-deleted' },
            });
            return;
          }

          // Compute and store ack hash
          const ackHash = computeMarketSnapshotFieldsHash(freshSnapshotSettings.snapshotFields);
          const ackSuccess = await sessionDraftService.setMarketSnapshotFieldsAckHash(
            draftId,
            ackHash
          );
          if (!ackSuccess) {
            hasRedirectedRef.current = true;
            setIsRedirecting(true);
            navigate('/dashboard', {
              replace: true,
              state: { openSessionModal: true, reason: 'session-deleted' },
            });
            return;
          }

          // Update local market states with new field config and purge orphaned data
          const validFieldIds = new Set(newFieldConfig.map((f) => f.fieldId));
          const now = new Date().toISOString();
          const updatedMarketStates = marketStatesValues.map((state) => ({
            ...state,
            fieldConfigSnapshot: newFieldConfig,
            updatedAt: now,
            snapshots: state.snapshots.map((snapshot) => ({
              ...snapshot,
              data: Object.fromEntries(
                Object.entries(snapshot.data).filter(([fieldId]) => validFieldIds.has(fieldId))
              ),
            })),
          }));
          setMarketStatesValues(updatedMarketStates);
          appliedAny = true;
          anySettingsApplied = true;
        } else {
          // Keep current - just set ack hash
          if (freshSnapshotSettings?.snapshotFields?.length) {
            const ackHash = computeMarketSnapshotFieldsHash(freshSnapshotSettings.snapshotFields);
            const success = await sessionDraftService.setMarketSnapshotFieldsAckHash(draftId, ackHash);
            if (!success) {
              hasRedirectedRef.current = true;
              setIsRedirecting(true);
              navigate('/dashboard', {
                replace: true,
                state: { openSessionModal: true, reason: 'session-deleted' },
              });
              return;
            }
          } else {
            showToast({
              title: 'Settings unavailable',
              description: 'Cannot acknowledge snapshot field changes without settings.',
              color: 'danger',
            });
            setIsUpdatingDraftConfig(false);
            return;
          }
        }
      }

      // Process finalCheckItems section if diverged
      if (divergedSections.includes('finalCheckItems')) {
        const resolution = sectionResolutions['finalCheckItems'] ?? 'apply';

        if (resolution === 'apply') {
          if (!freshSettings) {
            showToast({
              title: 'Settings unavailable',
              description: 'Cannot update final checks without pre-trading settings.',
              color: 'danger',
            });
            setIsUpdatingDraftConfig(false);
            return;
          }

          // Update finalCheckItems config (migrates values)
          const updateSuccess = await sessionDraftService.updateFinalCheckItemsConfig(
            draftId,
            freshSettings.finalCheckItems
          );
          if (!updateSuccess) {
            hasRedirectedRef.current = true;
            setIsRedirecting(true);
            navigate('/dashboard', {
              replace: true,
              state: { openSessionModal: true, reason: 'session-deleted' },
            });
            return;
          }
          appliedAny = true;
          anySettingsApplied = true;
        }

        // Set ack hash (both apply and keep)
        if (freshSettings) {
          const ackHash = computeFinalCheckItemsHash(freshSettings);
          const ackSuccess = await sessionDraftService.setFinalCheckItemsAckHash(draftId, ackHash);
          if (!ackSuccess) {
            hasRedirectedRef.current = true;
            setIsRedirecting(true);
            navigate('/dashboard', {
              replace: true,
              state: { openSessionModal: true, reason: 'session-deleted' },
            });
            return;
          }
        } else if (resolution === 'keep') {
          showToast({
            title: 'Settings unavailable',
            description: 'Cannot acknowledge final checks changes without settings.',
            color: 'danger',
          });
          setIsUpdatingDraftConfig(false);
          return;
        }
      }

      // Process tradingSetup section if diverged
      if (divergedSections.includes('tradingSetup')) {
        const resolution = sectionResolutions['tradingSetup'] ?? 'apply';
        const freshTradingSetupSettings = await tradingSetupSettingsService.getCurrentSettings();

        // Resolve effective account ID once for both apply and ack hash paths.
        // Settings may store undefined for single-account users who never explicitly
        // configured a default — resolve using the same single-account fallback.
        let resolvedFreshSettings = freshTradingSetupSettings;
        if (freshTradingSetupSettings.defaultAccountId === undefined) {
          const activeAccounts = await accountService.getActiveAccounts();
          const singleAccount = activeAccounts.length === 1 ? activeAccounts[0] : undefined;
          if (singleAccount) {
            resolvedFreshSettings = { ...freshTradingSetupSettings, defaultAccountId: singleAccount.id };
          }
        }

        if (resolution === 'apply') {
          const updateSuccess = await sessionDraftService.updateTradingSetup(
            draftId,
            resolvedFreshSettings.defaultStrategyPlaybookPairs,
            resolvedFreshSettings.defaultAccountId,
            resolvedFreshSettings.defaultFocusInstrumentIds,
            undefined,
            false,
            undefined,
            undefined,
            undefined,
            undefined
          );
          if (!updateSuccess) {
            hasRedirectedRef.current = true;
            setIsRedirecting(true);
            navigate('/dashboard', {
              replace: true,
              state: { openSessionModal: true, reason: 'session-deleted' },
            });
            return;
          }
          appliedAny = true;
          anySettingsApplied = true;
        }

        // Set ack hash (both apply and keep) — use resolved settings so the hash
        // matches what the draft will contain after auto-emit resolves the account
        const ackHash = computeTradingSetupHash(resolvedFreshSettings);
        const ackSuccess = await sessionDraftService.setTradingSetupAckHash(draftId, ackHash);
        if (!ackSuccess) {
          hasRedirectedRef.current = true;
          setIsRedirecting(true);
          navigate('/dashboard', {
            replace: true,
            state: { openSessionModal: true, reason: 'session-deleted' },
          });
          return;
        }
      }

      // Process riskProfileDefault section if diverged
      if (divergedSections.includes('riskProfileDefault')) {
        const riskProfileResolution = sectionResolutions['riskProfileDefault'] ?? 'apply';

        // Determine the post-resolution account: if tradingSetup was also diverged
        // and resolved with 'apply', fetch fresh settings to get the new account;
        // otherwise use the draft's current account
        let postResolutionAccountId: string | undefined;
        if (divergedSections.includes('tradingSetup') && (sectionResolutions['tradingSetup'] ?? 'apply') === 'apply') {
          // tradingSetup was applied — the account may have changed.
          // Fetch fresh settings and resolve effective account (same as tradingSetup block)
          const freshSettings = await tradingSetupSettingsService.getCurrentSettings();
          postResolutionAccountId = freshSettings.defaultAccountId;
          if (!postResolutionAccountId) {
            const activeAccounts = await accountService.getActiveAccounts();
            const singleAccount = activeAccounts.length === 1 ? activeAccounts[0] : undefined;
            if (singleAccount) {
              postResolutionAccountId = singleAccount.id;
            }
          }
        } else {
          // tradingSetup not diverged or kept — draft account unchanged
          const currentDraft = await sessionDraftService.getById(draftId);
          postResolutionAccountId = currentDraft?.defaultAccountId;
        }

        if (postResolutionAccountId) {
          // Fetch AccountRisk for the post-resolution account
          const riskConfigs = await accountRiskService.getActiveByAccountId(postResolutionAccountId);
          const canonicalConfig = riskConfigs.length > 0
            ? riskConfigs.reduce((oldest, c) => (c.createdAt < oldest.createdAt ? c : oldest))
            : undefined;

          // Resolve effective default profile
          let effectiveDefaultId: string | undefined;
          if (canonicalConfig) {
            const activeProfiles = canonicalConfig.riskProfiles.filter((p) =>
              isEntityActive(p.status)
            );
            if (canonicalConfig.defaultProfileId) {
              const defaultProfile = activeProfiles.find(
                (p) => p.id === canonicalConfig.defaultProfileId
              );
              if (defaultProfile) {
                effectiveDefaultId = defaultProfile.id;
              }
            }
            if (!effectiveDefaultId) {
              effectiveDefaultId = activeProfiles[0]?.id;
            }
          }

          if (riskProfileResolution === 'apply') {
            // Apply: switch to the new effective default profile
            // Read the current draft to preserve all other fields
            const currentDraftForApply = await sessionDraftService.getById(draftId);
            if (currentDraftForApply) {
              const updateSuccess = await sessionDraftService.updateTradingSetup(
                draftId,
                currentDraftForApply.activePlaybookSelections ?? [],
                currentDraftForApply.defaultAccountId,
                currentDraftForApply.focusInstrumentIds,
                undefined, // clear riskSnapshot
                false,     // clear setupConfirmed
                effectiveDefaultId,
                effectiveDefaultId ? 'default' : undefined,
                undefined, // clear session overrides on profile change
                undefined
              );
              if (!updateSuccess) {
                hasRedirectedRef.current = true;
                setIsRedirecting(true);
                navigate('/dashboard', {
                  replace: true,
                  state: { openSessionModal: true, reason: 'session-deleted' },
                });
                return;
              }
              appliedAny = true;
              anySettingsApplied = true;
            }
          } else {
            // Keep: preserve pre-resolution selectedRiskProfileId, change source to 'manual'.
            // Must use preResolutionProfileId (captured before any handlers ran) because
            // tradingSetup apply may have already cleared the profile ID in Dexie.
            const currentDraftForKeep = await sessionDraftService.getById(draftId);
            if (currentDraftForKeep) {
              const updateSuccess = await sessionDraftService.updateTradingSetup(
                draftId,
                currentDraftForKeep.activePlaybookSelections ?? [],
                currentDraftForKeep.defaultAccountId,
                currentDraftForKeep.focusInstrumentIds,
                currentDraftForKeep.riskSnapshot,
                currentDraftForKeep.setupConfirmed ?? false,
                preResolutionProfileId, // preserve pre-modal profile ID
                'manual', // flip source to manual — user chose to keep old profile
                currentDraftForKeep.sessionStrategyOverrides,
                currentDraftForKeep.sessionPlaybookOverrides
              );
              if (!updateSuccess) {
                hasRedirectedRef.current = true;
                setIsRedirecting(true);
                navigate('/dashboard', {
                  replace: true,
                  state: { openSessionModal: true, reason: 'session-deleted' },
                });
                return;
              }
              appliedAny = true;
            }
          }

          // Set ack hash (both apply and keep)
          const riskAckHash = computeRiskProfileDefaultHash(effectiveDefaultId);
          const riskAckSuccess = await sessionDraftService.setRiskProfileDefaultAckHash(draftId, riskAckHash);
          if (!riskAckSuccess) {
            hasRedirectedRef.current = true;
            setIsRedirecting(true);
            navigate('/dashboard', {
              replace: true,
              state: { openSessionModal: true, reason: 'session-deleted' },
            });
            return;
          }
        }
      }

      // Reload draft if any section was applied (to get migrated values)
      if (appliedAny && (
        divergedSections.includes('healthMetrics') ||
        divergedSections.includes('personalPrep') ||
        divergedSections.includes('sectionVisibility') ||
        divergedSections.includes('finalCheckItems') ||
        divergedSections.includes('tradingSetup') ||
        divergedSections.includes('riskProfileDefault')
      )) {
        const updatedDraft = await sessionDraftService.getById(draftId);
        if (updatedDraft) {
          setHealthMetricsValues(updatedDraft.healthMetrics ?? {});
          setPersonalPrepValues(updatedDraft.personalPrep ?? {});
          setFinalChecksValues(updatedDraft.finalChecks ?? []);
          if (updatedDraft.preTradingConfig) {
            setDraftConfig(updatedDraft.preTradingConfig);
          }
          // Rehydrate trading setup state based on resolution
          if (divergedSections.includes('tradingSetup')) {
            const tradingSetupResolution = sectionResolutions['tradingSetup'] ?? 'apply';
            if (tradingSetupResolution === 'apply') {
              // Apply: draft values changed, rehydrate all state and clear snapshot anchor
              setActivePlaybookSelections(updatedDraft.activePlaybookSelections ?? []);
              setDefaultAccountIdValue(updatedDraft.defaultAccountId);
              setFocusInstrumentIdsValues(updatedDraft.focusInstrumentIds ?? []);
              setRiskSnapshotValue(updatedDraft.riskSnapshot);
              setSetupConfirmedValue(updatedDraft.setupConfirmed ?? false);
              // Reset profile — new account may have different profiles
              setSelectedRiskProfileId(undefined);
              setSelectedRiskProfileSource(undefined);
              setSessionStrategyOverrides([]);
              setSessionPlaybookOverrides([]);
              confirmedSnapshotRef.current = null;
              confirmedRiskSnapshotRef.current = null;
            } else if (updatedDraft.setupConfirmed) {
              // Keep + still confirmed: rebuild snapshot anchor from reloaded draft values
              confirmedSnapshotRef.current = canonicalizeSnapshot(
                updatedDraft.activePlaybookSelections ?? [],
                updatedDraft.defaultAccountId,
                updatedDraft.focusInstrumentIds ?? [],
                updatedDraft.selectedRiskProfileId,
                updatedDraft.sessionStrategyOverrides,
                updatedDraft.sessionPlaybookOverrides,
              );
              confirmedRiskSnapshotRef.current = updatedDraft.riskSnapshot ?? null;
            }
            // Keep + not confirmed: confirmedSnapshotRef is already correct (no values changed)
          }
          // Rehydrate risk profile state based on riskProfileDefault resolution
          if (divergedSections.includes('riskProfileDefault')) {
            const riskProfileResolution = sectionResolutions['riskProfileDefault'] ?? 'apply';
            if (riskProfileResolution === 'apply') {
              // Apply: profile changed to new effective default
              setSelectedRiskProfileId(updatedDraft.selectedRiskProfileId);
              setSelectedRiskProfileSource(updatedDraft.selectedRiskProfileSource);
              setRiskSnapshotValue(undefined);
              setSetupConfirmedValue(false);
              setSessionStrategyOverrides([]);
              setSessionPlaybookOverrides([]);
              confirmedSnapshotRef.current = null;
              confirmedRiskSnapshotRef.current = null;
            } else {
              // Keep: source flipped to 'manual', restore profile ID from draft
              // (may have been cleared by tradingSetup apply rehydration above)
              setSelectedRiskProfileId(updatedDraft.selectedRiskProfileId);
              setSelectedRiskProfileSource('manual');
            }
          }
        }
      }

      // Clear state and close modal
      setDivergedSections([]);
      setSectionResolutions({});
      setShowSettingsChangedModal(false);
      setIsUpdatingDraftConfig(false);

      // Show toast only if any section actually applied current settings
      if (anySettingsApplied) {
        showToast({
          title: 'Draft updated',
          description: 'Your draft now uses current settings.',
          color: 'success',
        });
      }
    } catch (err) {
      console.error('Failed to resolve settings divergence:', err);
      showToast({
        title: 'Failed to save changes',
        description: 'Please try again.',
        color: 'danger',
      });
      setIsUpdatingDraftConfig(false);
      // Keep modal open on error
    }
  }, [draftId, divergedSections, sectionResolutions, marketStatesValues, navigate, showToast]);

  /** Open Health Metrics config modal - re-fetches global settings for freshness */
  const handleOpenHealthMetricsConfig = useCallback(async () => {
    // Guard: block if other modals are open
    if (showSettingsChangedModal || showRecoveryModal) return;

    // Re-fetch global settings to handle cross-tab changes
    try {
      const freshSettings = await preTradingSettingsService.getCurrentSettings();
      setGlobalSettings(freshSettings);
    } catch (err) {
      console.error('Failed to refresh settings:', err);
      showToast({
        title: 'Cannot open settings',
        description: 'Failed to load current settings.',
        color: 'danger',
      });
      return;
    }

    setShowHealthMetricsConfigModal(true);
  }, [showSettingsChangedModal, showRecoveryModal, showToast]);

  /** Handle successful save from Health Metrics config modal */
  const handleHealthMetricsConfigSuccess = useCallback(
    (
      updatedConfig: PreTradingConfigSnapshot,
      migratedHealthMetrics: Record<string, number> | undefined
    ) => {
      // Update draft config state for rendering
      setDraftConfig(updatedConfig);

      // Update health metrics values (may have been migrated)
      setHealthMetricsValues(migratedHealthMetrics ?? {});
    },
    []
  );

  /** Open Personal Prep Fields config modal - re-fetches global settings for freshness */
  const handleOpenPersonalPrepConfig = useCallback(async () => {
    // Guard: block if other modals are open
    if (showSettingsChangedModal || showRecoveryModal) return;

    // Re-fetch global settings to handle cross-tab changes
    try {
      const freshSettings = await preTradingSettingsService.getCurrentSettings();
      setGlobalSettings(freshSettings);
    } catch (err) {
      console.error('Failed to refresh settings:', err);
      showToast({
        title: 'Cannot open settings',
        description: 'Failed to load current settings.',
        color: 'danger',
      });
      return;
    }

    setShowPersonalPrepConfigModal(true);
  }, [showSettingsChangedModal, showRecoveryModal, showToast]);

  /** Handle successful save from Personal Prep Fields config modal */
  const handlePersonalPrepConfigSuccess = useCallback(
    (
      updatedConfig: PreTradingConfigSnapshot,
      migratedPersonalPrep: Record<string, boolean | string | number> | undefined
    ) => {
      // Update draft config state for rendering
      setDraftConfig(updatedConfig);

      // Update personal prep values (may have been migrated)
      setPersonalPrepValues(migratedPersonalPrep ?? {});
    },
    []
  );

  /** Open Objectives config modal - re-fetches global settings for freshness */
  const handleOpenObjectivesConfig = useCallback(async () => {
    // Guard: block if other modals are open
    if (showSettingsChangedModal || showRecoveryModal) return;

    // Re-fetch global settings to handle cross-tab changes
    try {
      const freshSettings = await preTradingSettingsService.getCurrentSettings();
      setGlobalSettings(freshSettings);
    } catch (err) {
      console.error('Failed to refresh settings:', err);
      showToast({
        title: 'Cannot open settings',
        description: 'Failed to load current settings.',
        color: 'danger',
      });
      return;
    }

    setShowObjectivesConfigModal(true);
  }, [showSettingsChangedModal, showRecoveryModal, showToast]);

  /** Handle successful save from Objectives config modal */
  const handleObjectivesConfigSuccess = useCallback(
    (updatedMaxObjectives: number) => {
      // Update draft config state for rendering
      setDraftConfig((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          maxObjectives: updatedMaxObjectives,
        };
      });

      // Handle maxObjectives reduction - truncate selections if needed
      if (objectivesValues.length > updatedMaxObjectives) {
        const now = new Date().toISOString();
        const truncated = objectivesValues.slice(0, updatedMaxObjectives);
        // Recalculate isPrimary (first item is primary)
        const updated = truncated.map((draft, index) => ({
          ...draft,
          isPrimary: index === 0,
          updatedAt: now,
        }));
        setObjectivesValues(updated);

        // Update pendingValuesRef if active
        if (pendingValuesRef.current) {
          pendingValuesRef.current = {
            ...pendingValuesRef.current,
            objectives: updated,
          };
        }

        // Show warning toast
        const removedCount = objectivesValues.length - updatedMaxObjectives;
        showToast({
          title: 'Objectives truncated',
          description: `${removedCount} objective(s) removed due to reduced limit.`,
          color: 'warning',
        });
      }
    },
    [objectivesValues, showToast]
  );

  /** Open Market Snapshot Fields config modal - re-fetches global settings for freshness */
  const handleOpenMarketSnapshotFieldsConfig = useCallback(async () => {
    // Guard: block if other modals are open
    if (showSettingsChangedModal || showRecoveryModal) return;

    // Re-fetch global settings to handle cross-tab changes
    try {
      const freshSettings = await marketSnapshotSettingsService.getCurrentSettings();
      if (!freshSettings) {
        showToast({
          title: 'Cannot open settings',
          description: 'Market snapshot settings not found.',
          color: 'danger',
        });
        return;
      }
      setGlobalMarketSnapshotSettings(freshSettings);
    } catch (err) {
      console.error('Failed to refresh market snapshot settings:', err);
      showToast({
        title: 'Cannot open settings',
        description: 'Failed to load current settings.',
        color: 'danger',
      });
      return;
    }

    setShowMarketSnapshotFieldsConfigModal(true);
  }, [showSettingsChangedModal, showRecoveryModal, showToast]);

  /** Handle successful save from Market Snapshot Fields config modal */
  const handleMarketSnapshotFieldsConfigSuccess = useCallback(
    (updatedFieldConfig: MarketStateDraft['fieldConfigSnapshot']) => {
      // Build set of valid field IDs for purging orphaned data
      const validFieldIds = new Set(updatedFieldConfig.map((f) => f.fieldId));
      const now = new Date().toISOString();

      // Update all market states with new field config and purge orphaned data
      const updatedMarketStates = marketStatesValues.map((state) => ({
        ...state,
        fieldConfigSnapshot: updatedFieldConfig,
        updatedAt: now,
        // Purge orphaned data from snapshots (mirrors service behavior)
        snapshots: state.snapshots.map((snapshot) => ({
          ...snapshot,
          data: Object.fromEntries(
            Object.entries(snapshot.data).filter(([fieldId]) => validFieldIds.has(fieldId))
          ),
        })),
      }));

      setMarketStatesValues(updatedMarketStates);
    },
    [marketStatesValues]
  );

  /** Open Economic Calendar config modal - guards against other modals being open */
  const handleOpenEconomicCalendarConfig = useCallback(() => {
    if (showSettingsChangedModal || showRecoveryModal) return;
    setShowEconomicCalendarConfigModal(true);
  }, [showSettingsChangedModal, showRecoveryModal]);

  /** Handle successful save from Economic Calendar config modal - triggers settings refresh in EconomicCalendarStep */
  const handleEconomicCalendarSettingsSaved = useCallback(() => {
    setEconomicCalendarSettingsKey((prev) => prev + 1);
  }, []);

  /** Open Final Checks config modal - re-fetches global settings for freshness */
  const handleOpenFinalChecksConfig = useCallback(async () => {
    // Guard: block if other modals are open
    if (showSettingsChangedModal || showRecoveryModal) return;

    // Re-fetch global settings to handle cross-tab changes
    try {
      const freshSettings = await preTradingSettingsService.getCurrentSettings();
      setGlobalSettings(freshSettings);
    } catch (err) {
      console.error('Failed to refresh settings:', err);
      showToast({
        title: 'Cannot open settings',
        description: 'Failed to load current settings.',
        color: 'danger',
      });
      return;
    }

    setShowFinalChecksConfigModal(true);
  }, [showSettingsChangedModal, showRecoveryModal, showToast]);

  /** Handle successful save from Final Checks config modal */
  const handleFinalChecksConfigSuccess = useCallback(
    (
      updatedConfig: PreTradingConfigSnapshot,
      migratedFinalChecks: FinalCheckEntry[] | undefined
    ) => {
      // Update draft config state for rendering
      setDraftConfig(updatedConfig);

      // Update final checks values (may have been migrated)
      setFinalChecksValues(migratedFinalChecks ?? []);
    },
    []
  );

  /** Open Trading Setup Config modal (T11) — re-fetches global settings for freshness */
  const handleOpenTradingSetupConfig = useCallback(async () => {
    // Guard: block if other modals are open
    if (showSettingsChangedModal || showRecoveryModal) return;

    // Re-fetch global settings to handle cross-tab changes
    try {
      const freshSettings = await tradingSetupSettingsService.getCurrentSettings();
      setGlobalTradingSetupSettings(freshSettings);
    } catch (err) {
      console.error('Failed to refresh trading setup settings:', err);
      showToast({
        title: 'Cannot open settings',
        description: 'Failed to load current settings.',
        color: 'danger',
      });
      return;
    }

    setShowTradingSetupConfigModal(true);
  }, [showSettingsChangedModal, showRecoveryModal, showToast]);

  /**
   * Handle save from Trading Setup Config modal (T11).
   * 1. Persists defaults to global settings
   * 2. Evaluates per-section resolution (ignore / apply silently / prompt)
   * 3. If prompt sections exist, opens resolution prompt; otherwise applies silently
   */
  const handleTradingSetupConfigSave = useCallback(async (output: TradingSetupDefaultsUpdate) => {
    if (!globalTradingSetupSettings) {
      throw new Error('Trading setup settings not loaded');
    }
    if (!draftId) {
      throw new Error('No active draft');
    }

    // 1. Persist defaults to global settings
    await tradingSetupSettingsService.saveDefaults(globalTradingSetupSettings.id, output);

    // 2. Resolve effective account IDs for old and new defaults
    //    (single-account fallback: undefined → sole active account ID)
    let oldEffectiveAccountId = globalTradingSetupSettings.defaultAccountId;
    let newEffectiveAccountId = 'defaultAccountId' in output ? output.defaultAccountId : undefined;
    const activeAccounts = await accountService.getActiveAccounts();
    const singleAccount = activeAccounts.length === 1 ? activeAccounts[0] : undefined;
    if (oldEffectiveAccountId === undefined && singleAccount) {
      oldEffectiveAccountId = singleAccount.id;
    }
    if (newEffectiveAccountId === undefined && singleAccount) {
      newEffectiveAccountId = singleAccount.id;
    }

    // 3. Build resolution input and evaluate
    const resolutionResult = resolveTradingSetupDefaults({
      oldDefaults: {
        pairs: globalTradingSetupSettings.defaultStrategyPlaybookPairs,
        effectiveAccountId: oldEffectiveAccountId,
        focusInstrumentIds: globalTradingSetupSettings.defaultFocusInstrumentIds,
      },
      newDefaults: {
        pairs: output.defaultStrategyPlaybookPairs,
        effectiveAccountId: newEffectiveAccountId,
        focusInstrumentIds: output.defaultFocusInstrumentIds,
      },
      currentSession: {
        pairs: activePlaybookSelections,
        effectiveAccountId: defaultAccountIdValue,
        focusInstrumentIds: focusInstrumentIdsValues,
      },
    });

    // Refresh globalTradingSetupSettings to reflect saved defaults
    const freshSettings = await tradingSetupSettingsService.getCurrentSettings();
    setGlobalTradingSetupSettings(freshSettings);

    // Check if any section needs action
    const hasApplySections =
      resolutionResult.strategyPlaybookPairs.decision === 'apply' ||
      resolutionResult.account.decision === 'apply' ||
      resolutionResult.focusInstruments.decision === 'apply';

    // 4a. All ignore — just set ack hash, no session changes
    if (!hasApplySections && !resolutionResult.hasPromptSections) {
      // Resolve effective account for ack hash computation
      let resolvedFreshSettings = freshSettings;
      if (freshSettings.defaultAccountId === undefined && singleAccount) {
        resolvedFreshSettings = { ...freshSettings, defaultAccountId: singleAccount.id };
      }
      const ackHash = computeTradingSetupHash(resolvedFreshSettings);
      await sessionDraftService.setTradingSetupAckHash(draftId, ackHash);
      return;
    }

    // 4b. Has prompt sections — show resolution prompt
    if (resolutionResult.hasPromptSections) {
      // Build human-readable value summaries for prompted sections
      const summaries: Partial<Record<TradingSetupSectionId, { newDefault: string; currentSession: string }>> = {};
      const sections: TradingSetupSectionId[] = ['strategyPlaybookPairs', 'account', 'focusInstruments'];

      for (const sectionId of sections) {
        if (resolutionResult[sectionId].decision !== 'prompt') continue;

        if (sectionId === 'strategyPlaybookPairs') {
          const newCount = output.defaultStrategyPlaybookPairs.length;
          const currentCount = activePlaybookSelections.length;
          summaries.strategyPlaybookPairs = {
            newDefault: newCount === 0 ? 'None' : `${newCount} pair${newCount !== 1 ? 's' : ''}`,
            currentSession: currentCount === 0 ? 'None' : `${currentCount} pair${currentCount !== 1 ? 's' : ''}`,
          };
        } else if (sectionId === 'account') {
          const findAccountName = (id: string | undefined) => {
            if (!id) return 'No default account';
            const account = activeAccounts.find((a) => a.id === id);
            return account ? account.name : id;
          };
          summaries.account = {
            newDefault: findAccountName(newEffectiveAccountId),
            currentSession: findAccountName(defaultAccountIdValue),
          };
        } else if (sectionId === 'focusInstruments') {
          const resolveSymbols = async (ids: string[]): Promise<string> => {
            if (ids.length === 0) return 'None';
            const symbols: string[] = [];
            for (const id of ids) {
              const instrument = await instrumentService.get(id);
              symbols.push(instrument ? instrument.symbol : id);
            }
            return symbols.join(', ');
          };
          summaries.focusInstruments = {
            newDefault: await resolveSymbols(output.defaultFocusInstrumentIds),
            currentSession: await resolveSymbols(focusInstrumentIdsValues),
          };
        }
      }

      // Initialize choices: 'apply' for prompted sections
      const initialChoices: Partial<Record<TradingSetupSectionId, 'apply' | 'keep'>> = {};
      for (const sectionId of sections) {
        if (resolutionResult[sectionId].decision === 'prompt') {
          initialChoices[sectionId] = 'apply';
        }
      }

      setTradingSetupResolutionResult(resolutionResult);
      setTradingSetupResolutionNewDefaults(output);
      setTradingSetupResolutionSummaries(summaries);
      setTradingSetupResolutionChoices(initialChoices);
      setShowTradingSetupResolutionPrompt(true);
      // Config modal will close on its own (onSave returned successfully)
      return;
    }

    // 4c. No prompt sections but has apply sections — apply silently
    //     Build merged values: use new default for 'apply' sections, current session for 'ignore'
    const mergedPairs = resolutionResult.strategyPlaybookPairs.decision === 'apply'
      ? output.defaultStrategyPlaybookPairs
      : activePlaybookSelections;
    const mergedAccountId = resolutionResult.account.decision === 'apply'
      ? newEffectiveAccountId
      : defaultAccountIdValue;
    const mergedInstruments = resolutionResult.focusInstruments.decision === 'apply'
      ? output.defaultFocusInstrumentIds
      : focusInstrumentIdsValues;

    const accountApplied = resolutionResult.account.decision === 'apply';

    const updateSuccess = await sessionDraftService.updateTradingSetup(
      draftId,
      mergedPairs,
      mergedAccountId,
      mergedInstruments.length > 0 ? mergedInstruments : undefined,
      undefined,   // always clear riskSnapshot (any apply invalidates confirmation)
      false,       // always clear setupConfirmed
      accountApplied ? undefined : selectedRiskProfileId,
      accountApplied ? undefined : selectedRiskProfileSource,
      accountApplied ? undefined : (sessionStrategyOverrides.length > 0 ? sessionStrategyOverrides : undefined),
      accountApplied ? undefined : (sessionPlaybookOverrides.length > 0 ? sessionPlaybookOverrides : undefined)
    );
    if (!updateSuccess) {
      hasRedirectedRef.current = true;
      setIsRedirecting(true);
      navigate('/dashboard', {
        replace: true,
        state: { openSessionModal: true, reason: 'session-deleted' },
      });
      return;
    }

    // Set ack hash
    let resolvedFreshSettings = freshSettings;
    if (freshSettings.defaultAccountId === undefined && singleAccount) {
      resolvedFreshSettings = { ...freshSettings, defaultAccountId: singleAccount.id };
    }
    const ackHash = computeTradingSetupHash(resolvedFreshSettings);
    const ackSuccess = await sessionDraftService.setTradingSetupAckHash(draftId, ackHash);
    if (!ackSuccess) {
      hasRedirectedRef.current = true;
      setIsRedirecting(true);
      navigate('/dashboard', {
        replace: true,
        state: { openSessionModal: true, reason: 'session-deleted' },
      });
      return;
    }

    // Rehydrate local state from draft
    const updatedDraft = await sessionDraftService.getById(draftId);
    if (updatedDraft) {
      setActivePlaybookSelections(updatedDraft.activePlaybookSelections ?? []);
      setDefaultAccountIdValue(updatedDraft.defaultAccountId);
      setFocusInstrumentIdsValues(updatedDraft.focusInstrumentIds ?? []);
      // Always clear confirmation state (any apply invalidates)
      setRiskSnapshotValue(undefined);
      setSetupConfirmedValue(false);
      confirmedSnapshotRef.current = null;
      confirmedRiskSnapshotRef.current = null;
      // Preserve risk profile/overrides from draft (only cleared when account applied)
      setSelectedRiskProfileId(updatedDraft.selectedRiskProfileId);
      setSelectedRiskProfileSource(updatedDraft.selectedRiskProfileSource);
      setSessionStrategyOverrides(updatedDraft.sessionStrategyOverrides ?? []);
      setSessionPlaybookOverrides(updatedDraft.sessionPlaybookOverrides ?? []);
    }

    showToast({
      title: 'Defaults updated',
      description: 'Your session has been updated to match the new defaults.',
      color: 'success',
    });
  }, [globalTradingSetupSettings, draftId, activePlaybookSelections, defaultAccountIdValue, focusInstrumentIdsValues, selectedRiskProfileId, selectedRiskProfileSource, sessionStrategyOverrides, sessionPlaybookOverrides, navigate, showToast]);

  /**
   * Handle confirmation of the Trading Setup Resolution Prompt (T11 Phase 3).
   * Merges auto-apply decisions from the resolution engine with user choices for prompted sections,
   * builds merged values, persists to session draft, rehydrates local state, and sets ack hash.
   */
  const handleTradingSetupResolutionConfirm = useCallback(async () => {
    if (!tradingSetupResolutionResult || !tradingSetupResolutionNewDefaults || !draftId) return;

    setIsApplyingResolution(true);
    try {
      // Resolve effective new account ID (same single-account fallback)
      let newEffectiveAccountId = 'defaultAccountId' in tradingSetupResolutionNewDefaults
        ? tradingSetupResolutionNewDefaults.defaultAccountId
        : undefined;
      const activeAccounts = await accountService.getActiveAccounts();
      const singleAccount = activeAccounts.length === 1 ? activeAccounts[0] : undefined;
      if (newEffectiveAccountId === undefined && singleAccount) {
        newEffectiveAccountId = singleAccount.id;
      }

      // Determine final decision per section: engine decision for non-prompt, user choice for prompt
      const finalDecision = (sectionId: TradingSetupSectionId): 'apply' | 'keep' | 'ignore' => {
        const engineDecision = tradingSetupResolutionResult[sectionId].decision;
        if (engineDecision === 'prompt') {
          return tradingSetupResolutionChoices[sectionId] ?? 'apply';
        }
        return engineDecision; // 'apply' or 'ignore'
      };

      const pairsDecision = finalDecision('strategyPlaybookPairs');
      const accountDecision = finalDecision('account');
      const instrumentsDecision = finalDecision('focusInstruments');

      const anyApplied = pairsDecision === 'apply' || accountDecision === 'apply' || instrumentsDecision === 'apply';

      if (anyApplied) {
        const accountApplied = accountDecision === 'apply';

        // Build merged values
        const mergedPairs = pairsDecision === 'apply'
          ? tradingSetupResolutionNewDefaults.defaultStrategyPlaybookPairs
          : activePlaybookSelections;
        const mergedAccountId = accountDecision === 'apply'
          ? newEffectiveAccountId
          : defaultAccountIdValue;
        const mergedInstruments = instrumentsDecision === 'apply'
          ? tradingSetupResolutionNewDefaults.defaultFocusInstrumentIds
          : focusInstrumentIdsValues;

        const updateSuccess = await sessionDraftService.updateTradingSetup(
          draftId,
          mergedPairs,
          mergedAccountId,
          mergedInstruments.length > 0 ? mergedInstruments : undefined,
          undefined,   // always clear riskSnapshot (any apply invalidates confirmation)
          false,       // always clear setupConfirmed
          accountApplied ? undefined : selectedRiskProfileId,
          accountApplied ? undefined : selectedRiskProfileSource,
          accountApplied ? undefined : (sessionStrategyOverrides.length > 0 ? sessionStrategyOverrides : undefined),
          accountApplied ? undefined : (sessionPlaybookOverrides.length > 0 ? sessionPlaybookOverrides : undefined)
        );
        if (!updateSuccess) {
          hasRedirectedRef.current = true;
          setIsRedirecting(true);
          navigate('/dashboard', {
            replace: true,
            state: { openSessionModal: true, reason: 'session-deleted' },
          });
          return;
        }

        // Rehydrate local state from draft
        const updatedDraft = await sessionDraftService.getById(draftId);
        if (updatedDraft) {
          setActivePlaybookSelections(updatedDraft.activePlaybookSelections ?? []);
          setDefaultAccountIdValue(updatedDraft.defaultAccountId);
          setFocusInstrumentIdsValues(updatedDraft.focusInstrumentIds ?? []);
          // Always clear confirmation state (any apply invalidates)
          setRiskSnapshotValue(undefined);
          setSetupConfirmedValue(false);
          confirmedSnapshotRef.current = null;
          confirmedRiskSnapshotRef.current = null;
          // Preserve risk profile/overrides from draft (only cleared when account applied)
          setSelectedRiskProfileId(updatedDraft.selectedRiskProfileId);
          setSelectedRiskProfileSource(updatedDraft.selectedRiskProfileSource);
          setSessionStrategyOverrides(updatedDraft.sessionStrategyOverrides ?? []);
          setSessionPlaybookOverrides(updatedDraft.sessionPlaybookOverrides ?? []);
        }
      }

      // Set ack hash (both apply and keep paths) to prevent re-prompting
      const freshSettings = await tradingSetupSettingsService.getCurrentSettings();
      let resolvedFreshSettings = freshSettings;
      if (freshSettings.defaultAccountId === undefined && singleAccount) {
        resolvedFreshSettings = { ...freshSettings, defaultAccountId: singleAccount.id };
      }
      const ackHash = computeTradingSetupHash(resolvedFreshSettings);
      const ackSuccess = await sessionDraftService.setTradingSetupAckHash(draftId, ackHash);
      if (!ackSuccess) {
        hasRedirectedRef.current = true;
        setIsRedirecting(true);
        navigate('/dashboard', {
          replace: true,
          state: { openSessionModal: true, reason: 'session-deleted' },
        });
        return;
      }

      // Update global settings state
      setGlobalTradingSetupSettings(freshSettings);

      // Close resolution prompt, clear state
      setShowTradingSetupResolutionPrompt(false);
      setTradingSetupResolutionResult(null);
      setTradingSetupResolutionNewDefaults(null);
      setTradingSetupResolutionChoices({});
      setTradingSetupResolutionSummaries({});
      setIsApplyingResolution(false);

      if (anyApplied) {
        showToast({
          title: 'Defaults updated',
          description: 'Your session has been updated to match the new defaults.',
          color: 'success',
        });
      }
    } catch (err) {
      console.error('Failed to apply trading setup resolution:', err);
      showToast({
        title: 'Failed to apply changes',
        description: 'Please try again.',
        color: 'danger',
      });
      setIsApplyingResolution(false);
    }
  }, [tradingSetupResolutionResult, tradingSetupResolutionNewDefaults, tradingSetupResolutionChoices, draftId, activePlaybookSelections, defaultAccountIdValue, focusInstrumentIdsValues, selectedRiskProfileId, selectedRiskProfileSource, sessionStrategyOverrides, sessionPlaybookOverrides, navigate, showToast]);

  // Risk Detail Modal handlers
  const handleRiskTap = useCallback((context: { type: 'strategy' | 'playbook'; strategyId: string; playbookId?: string }) => {
    setRiskDetailContext(context);
    setShowRiskDetailModal(true);
  }, []);

  const handleRiskDetailClose = useCallback(() => {
    setShowRiskDetailModal(false);
    setRiskDetailContext(null);
  }, []);

  /**
   * Compute the correct setupConfirmed/riskSnapshot state for proposed override updates.
   * Passed to RiskDetailModal so it can persist the correct confirmation state
   * in the same Dexie transaction, keeping Dexie and React state coherent.
   */
  const computeConfirmationState = useCallback((updates: {
    sessionStrategyOverrides: SessionStrategyOverride[];
    sessionPlaybookOverrides: SessionPlaybookOverride[];
  }) => {
    const snapshot = confirmedSnapshotRef.current;
    if (snapshot) {
      const incoming = canonicalizeSnapshot(
        activePlaybookSelections,
        defaultAccountIdValue,
        focusInstrumentIdsValues,
        selectedRiskProfileId,
        updates.sessionStrategyOverrides,
        updates.sessionPlaybookOverrides,
      );
      if (snapshotsMatch(incoming, snapshot)) {
        return {
          setupConfirmed: true,
          riskSnapshot: confirmedRiskSnapshotRef.current ?? undefined,
        };
      }
    }
    return { setupConfirmed: false, riskSnapshot: undefined };
  }, [activePlaybookSelections, defaultAccountIdValue, focusInstrumentIdsValues, selectedRiskProfileId]);

  const handleRiskDetailSave = useCallback((updates: {
    sessionStrategyOverrides: SessionStrategyOverride[];
    sessionPlaybookOverrides: SessionPlaybookOverride[];
    setupConfirmed: boolean;
    riskSnapshot: TradingSetupRiskSnapshot | undefined;
  }) => {
    setSessionStrategyOverrides(updates.sessionStrategyOverrides);
    setSessionPlaybookOverrides(updates.sessionPlaybookOverrides);
    // Use the confirmation state computed by the modal (via computeConfirmationState)
    // to keep React state coherent with what was persisted to Dexie
    setSetupConfirmedValue(updates.setupConfirmed);
    setRiskSnapshotValue(updates.riskSnapshot);
    handleRiskDetailClose();
  }, [handleRiskDetailClose]);

  /** Flush pending auto-save and proceed to live trading */
  const flushAndProceedToLive = useCallback(() => {
    // Flush pending auto-save before phase change (may not unmount component)
    if (hasValidDraft) {
      // Clear timer to prevent duplicate save
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }

      // Flush pending values if any
      const pending = pendingValuesRef.current;
      if (pending) {
        const filteredHealth = filterHealthMetrics(pending.healthMetrics);
        const filteredPersonal = filterPersonalPrep(pending.personalPrep);

        // Fire-and-forget save for personalPrep/healthMetrics
        sessionDraftService.updatePersonalPrep(
          pending.draftId,
          filteredHealth,
          filteredPersonal
        );

        // Fire-and-forget save for objectives
        sessionDraftService.updateObjectives(pending.draftId, pending.objectives);

        // Fire-and-forget save for marketStates
        sessionDraftService.updateMarketStates(pending.draftId, pending.marketStates);

        // Fire-and-forget save for economicEvents
        sessionDraftService.updateEconomicEvents(pending.draftId, pending.economicEvents);

        // Fire-and-forget save for finalChecks
        sessionDraftService.updateFinalChecks(pending.draftId, pending.finalChecks);

        // Clear pending to prevent double flush on unmount
        pendingValuesRef.current = null;
      }
    }

    // Check if transition screen is enabled
    if (globalSettings?.enableTransitionScreen) {
      // Show transition modal - navigation happens when modal completes
      setShowTransitionScreenModal(true);
    } else {
      // Skip transition - proceed directly to live trading
      onPhaseChange('live-trading');
    }
  }, [hasValidDraft, globalSettings?.enableTransitionScreen, onPhaseChange]);

  /** Handle proceed to live trading - checks for objectives first */
  const handleProceedToLive = useCallback(() => {
    // Guard 1: Trading Setup must be complete (non-bypassable)
    const status = getTradingSetupCompletionStatus({
      activePlaybookSelections,
      defaultAccountId: defaultAccountIdValue,
      selectedRiskProfileId,
      setupConfirmed: setupConfirmedValue,
    });
    if (!status.isComplete) {
      setSetupCompletionStatus(status);
      setShowSetupIncompleteModal(true);
      return;
    }

    // Guard 2: Check if no objectives selected - show confirmation modal
    if (objectivesValues.length === 0) {
      setShowNoObjectiveModal(true);
      return;
    }

    // All checks passed - proceed directly
    flushAndProceedToLive();
  }, [activePlaybookSelections, defaultAccountIdValue, selectedRiskProfileId, setupConfirmedValue, objectivesValues.length, flushAndProceedToLive]);

  /** Handle confirmation from "No Objective" modal */
  const handleConfirmNoObjective = useCallback(() => {
    setShowNoObjectiveModal(false);
    flushAndProceedToLive();
  }, [flushAndProceedToLive]);


  /** Handle transition screen completion (auto-advance or button click) */
  const handleTransitionComplete = useCallback(() => {
    setShowTransitionScreenModal(false);
    onPhaseChange('live-trading');
  }, [onPhaseChange]);

  /** Bring a carry-forward draft into the current session */
  const handleBringIn = useCallback(
    async (draft: TradeSetupDraft) => {
      if (draftId === undefined) return;

      setCarryForwardSubmitting((prev) => new Set([...prev, draft.rootDraftId]));
      try {
        await sessionDraftService.materializeCarryForwardDraft(draft, draftId);
        setCarryForwardCandidates((prev) => prev.filter((d) => d.id !== draft.id));
        showToastRef.current({
          title: 'Draft added to session',
          description: `"${draft.tradeName ?? 'Unnamed trade'}" is now in your Live Trading trades.`,
          color: 'success',
        });
      } catch (err) {
        showToastRef.current({
          title: 'Failed to bring in draft',
          description: err instanceof Error ? err.message : 'Unknown error',
          color: 'danger',
        });
      } finally {
        setCarryForwardSubmitting((prev) => new Set([...prev].filter((id) => id !== draft.rootDraftId)));
      }
    },
    [draftId]
  );

  /** Dismiss a carry-forward draft for this session (not-now — will resurface next session) */
  const handleNotNow = useCallback((draft: TradeSetupDraft) => {
    // Remove from candidates list (no persistence — will resurface next session)
    setCarryForwardCandidates((prev) => prev.filter((d) => d.id !== draft.id));
    showToastRef.current({
      title: 'Skipped for now',
      description: `"${draft.tradeName ?? 'Unnamed trade'}" will reappear next session.`,
      color: 'default',
    });
  }, []);

  /** Delete a carry-forward draft forever — confirmed by ButtonGroup + Confirm flow */
  const handleDeleteForever = useCallback(
    async (draft: TradeSetupDraft) => {
      if (draftId === undefined) return;
      setCarryForwardSubmitting((prev) => new Set([...prev, draft.rootDraftId]));
      try {
        await retiredDraftLineageService.retire(draft.rootDraftId, draftId);
        setCarryForwardCandidates((prev) => prev.filter((d) => d.id !== draft.id));
        showToastRef.current({
          title: 'Draft permanently deleted',
          description: `"${draft.tradeName ?? 'Unnamed trade'}" will never appear as a carry-forward again.`,
          color: 'success',
        });
      } catch (err) {
        showToastRef.current({
          title: 'Failed to delete draft',
          description: err instanceof Error ? err.message : 'Unknown error',
          color: 'danger',
        });
      } finally {
        setCarryForwardSubmitting((prev) => new Set([...prev].filter((id) => id !== draft.rootDraftId)));
      }
    },
    [draftId]
  );

  /** T06 (WU2E): Execute the selected carry-forward action for a draft row */
  const handleConfirm = useCallback(
    async (draft: TradeSetupDraft): Promise<void> => {
      const action = selectedActions[draft.id];
      if (!action) return;
      if (action === 'bringIn') {
        await handleBringIn(draft);
      } else if (action === 'notNow') {
        handleNotNow(draft);
      } else {
        await handleDeleteForever(draft);
      }
    },
    [selectedActions, handleBringIn, handleNotNow, handleDeleteForever],
  );

  /** Navigate to a specific section */
  const handleNavigate = useCallback((sectionId: string) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const target = container.querySelector(`[data-section-id="${sectionId}"]`);
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  /** Handle "Go to Trading Setup" from setup-incomplete blocking modal */
  const handleSetupIncompleteGoToSetup = useCallback(() => {
    setShowSetupIncompleteModal(false);
    setSetupCompletionStatus(null);
    handleNavigate('tradingSetup');
  }, [handleNavigate]);

  /** Handle "Go to Objectives" from no-objective warning modal */
  const handleNoObjectiveGoToObjectives = useCallback(() => {
    setShowNoObjectiveModal(false);
    handleNavigate('objectives');
  }, [handleNavigate]);

  /**
   * Get reveal animation classes for a section
   * Uses motion-reduce utilities for accessibility (SSR-safe, declarative)
   */
  const getRevealClasses = useCallback(
    (sectionId: string): string => {
      const isRevealed = revealedSections.has(sectionId);
      const baseClasses = 'transition-all duration-700';
      const revealedClasses = 'opacity-100 translate-y-0';
      const hiddenClasses =
        'opacity-30 translate-y-8 motion-reduce:opacity-100 motion-reduce:translate-y-0';

      return `${baseClasses} ${isRevealed ? revealedClasses : hiddenClasses}`;
    },
    [revealedSections]
  );

  /**
   * Handle keyboard navigation between sections
   * Only handles keys when scroll container itself has focus (not child elements)
   * Navigates through visible sections only (respects sectionConfig visibility)
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Guard: only handle when scroll container has focus, not child elements
      // This prevents hijacking arrow keys in inputs, selects, buttons, etc.
      if (e.currentTarget !== e.target) {
        return;
      }

      const currentIndex = navSections.findIndex(
        (s) => s.id === currentSectionId
      );
      if (currentIndex === -1) return;

      let targetIndex: number | null = null;

      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        if (currentIndex < navSections.length - 1) {
          targetIndex = currentIndex + 1;
        }
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        if (currentIndex > 0) {
          targetIndex = currentIndex - 1;
        }
      }

      if (targetIndex !== null) {
        const targetSection = navSections[targetIndex];
        if (targetSection) {
          e.preventDefault();
          handleNavigate(targetSection.id);
        }
      }
    },
    [currentSectionId, handleNavigate, navSections]
  );

  /** IntersectionObserver to track current visible section and reveal animations */
  useEffect(() => {
    // Wait until loading is complete and scroll container is available
    if (isLoading || error) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Track best section for nav highlight (by intersection ratio)
        let bestSectionId: string | null = null;
        let bestRatio = 0;

        // Process all entries for reveal tracking and find best for nav
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.getAttribute('data-section-id');
            if (sectionId) {
              // Add to revealed sections (only updates if newly revealed)
              setRevealedSections((prev) =>
                prev.has(sectionId) ? prev : new Set(prev).add(sectionId)
              );

              // Track best section for current nav highlight
              if (entry.intersectionRatio > bestRatio) {
                bestRatio = entry.intersectionRatio;
                bestSectionId = sectionId;
              }
            }
          }
        });

        // Update current section for nav highlight
        if (bestSectionId) {
          setCurrentSectionId(bestSectionId);
        }
      },
      {
        root: container,
        threshold: [0.3, 0.5, 0.7], // Multiple thresholds for better snap detection
      }
    );

    // Observe all section elements
    const sections = container.querySelectorAll('[data-section-id]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [isLoading, error]); // Re-run when loading completes

  // Recovery modal - shown when auto-save fails due to deleted session
  // Rendered before early returns so it can overlay any state
  const recoveryModal = (
    <Modal
      isOpen={showRecoveryModal}
      isDismissable={false}
      hideCloseButton={true}
      backdrop="blur"
      size="md"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader>
              <span className="text-lg font-semibold">Session No Longer Persistent</span>
            </ModalHeader>
            <ModalBody>
              <div className="py-2 space-y-4">
                <p className="text-text-secondary">
                  This session was deleted by another tab or device.
                </p>
                <p className="text-text-secondary">
                  Click "Save Changes and Continue" to create a new session draft with your current data.
                </p>
                <p className="text-text-secondary">
                  Click "Go to Dashboard" to discard changes and return to the Main Dashboard.
                </p>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="flat"
                onPress={handleDiscardChanges}
                isDisabled={isSavingNewSession}
              >
                Go to Dashboard
              </Button>
              <Button
                color="primary"
                onPress={handleSaveAsNewSession}
                isLoading={isSavingNewSession}
              >
                Save Changes and Continue
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );

  // Section display name map for the modal
  const sectionDisplayNames: Record<SectionKey, string> = {
    healthMetrics: 'Health Metrics',
    personalPrep: 'Personal Preparation',
    sectionVisibility: 'Section Visibility & Order',
    snapshotFields: 'Snapshot Fields',
    finalCheckItems: 'Final Checks',
    tradingSetup: 'Trading Setup Defaults',
    riskProfileDefault: 'Default Risk Profile',
  };

  // Settings Changed modal - shown when draft config differs from current settings
  const settingsChangedModal = (
    <Modal
      isOpen={showSettingsChangedModal}
      isDismissable={false}
      hideCloseButton={true}
      backdrop="blur"
      size="md"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader>
              <span className="text-lg font-semibold">Settings Changed</span>
            </ModalHeader>
            <ModalBody>
              <div className="py-2 space-y-4">
                {/* Adaptive copy based on section count */}
                {divergedSections.length === 1 && divergedSections[0] ? (
                  <p className="text-text-secondary">
                    Your {sectionDisplayNames[divergedSections[0]].toLowerCase()} settings have
                    changed since this draft was created.
                  </p>
                ) : divergedSections.length > 1 ? (
                  <p className="text-text-secondary">
                    Your settings have changed in {divergedSections.length} areas since this draft
                    was created.
                  </p>
                ) : null}

                {/* Explanation of options */}
                <div className="text-sm text-text-secondary space-y-1">
                  <p>
                    <strong>Apply new settings:</strong> Update to current settings. Existing values
                    preserved where possible.
                  </p>
                  <p>
                    <strong>Keep current:</strong> Continue with draft&apos;s original settings.
                  </p>
                </div>

                {/* Apply All / Keep All convenience links (only show if multiple sections) */}
                {divergedSections.length > 1 && (
                  <div className="flex gap-4 text-sm">
                    <button
                      type="button"
                      className={
                        allApply
                          ? 'text-text-secondary cursor-default'
                          : 'text-primary hover:underline focus:outline-none'
                      }
                      onClick={handleApplyAll}
                      disabled={isUpdatingDraftConfig}
                      aria-disabled={allApply || isUpdatingDraftConfig}
                    >
                      Apply all
                    </button>
                    <button
                      type="button"
                      className={
                        allKept
                          ? 'text-text-secondary cursor-default'
                          : 'text-primary hover:underline focus:outline-none'
                      }
                      onClick={handleKeepAll}
                      disabled={isUpdatingDraftConfig}
                      aria-disabled={allKept || isUpdatingDraftConfig}
                    >
                      Keep all
                    </button>
                  </div>
                )}

                {/* Per-section resolution toggles */}
                <div className="space-y-4">
                  {divergedSections.map((section) => (
                    <div key={section} className="space-y-2">
                      <p className="text-sm font-medium text-text-primary">
                        {sectionDisplayNames[section]}
                      </p>
                      <RadioGroup
                        orientation="horizontal"
                        value={sectionResolutions[section] ?? 'apply'}
                        onValueChange={(value) => {
                          setSectionResolutions((prev) => ({
                            ...prev,
                            [section]: value as 'apply' | 'keep',
                          }));
                        }}
                        isDisabled={isUpdatingDraftConfig}
                        size="sm"
                      >
                        <Radio value="apply">Apply new settings</Radio>
                        <Radio value="keep">Keep current</Radio>
                      </RadioGroup>
                    </div>
                  ))}
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                color="primary"
                onPress={handleConfirmResolution}
                isLoading={isUpdatingDraftConfig}
              >
                Confirm
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );

  // Trading Setup Resolution Prompt — shown when per-section resolution has prompted sections
  const tradingSetupResolutionSectionNames: Record<TradingSetupSectionId, string> = {
    strategyPlaybookPairs: 'Strategy & Playbook Pairs',
    account: 'Default Account',
    focusInstruments: 'Focus Instruments',
  };

  const promptedSections: TradingSetupSectionId[] = tradingSetupResolutionResult
    ? (['strategyPlaybookPairs', 'account', 'focusInstruments'] as const).filter(
        (s) => tradingSetupResolutionResult[s].decision === 'prompt'
      )
    : [];

  const tradingSetupResolutionModal = (
    <Modal
      isOpen={showTradingSetupResolutionPrompt}
      isDismissable={false}
      hideCloseButton={true}
      backdrop="blur"
      size="md"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader>
              <span className="text-lg font-semibold">Apply Updated Defaults?</span>
            </ModalHeader>
            <ModalBody>
              <div className="py-2 space-y-4">
                <p className="text-text-secondary">
                  Your default trading setup has changed. Some of your current session values
                  differ from the new defaults.
                </p>

                <div className="text-sm text-text-secondary space-y-1">
                  <p>
                    <strong>Apply new default:</strong> Update your current session to use the
                    new default value.
                  </p>
                  <p>
                    <strong>Keep current:</strong> Continue with your current session value.
                  </p>
                </div>

                <div className="space-y-4">
                  {promptedSections.map((sectionId) => {
                    const summary = tradingSetupResolutionSummaries[sectionId];
                    return (
                      <div key={sectionId} className="space-y-2">
                        <p className="text-sm font-medium text-text-primary">
                          {tradingSetupResolutionSectionNames[sectionId]}
                        </p>
                        {summary && (
                          <div className="text-sm text-text-secondary space-y-0.5 pl-2">
                            <p>New default: {summary.newDefault}</p>
                            <p>Current session: {summary.currentSession}</p>
                          </div>
                        )}
                        <RadioGroup
                          orientation="horizontal"
                          value={tradingSetupResolutionChoices[sectionId] ?? 'apply'}
                          onValueChange={(value) => {
                            setTradingSetupResolutionChoices((prev) => ({
                              ...prev,
                              [sectionId]: value as 'apply' | 'keep',
                            }));
                          }}
                          isDisabled={isApplyingResolution}
                          size="sm"
                        >
                          <Radio value="apply">Apply new default</Radio>
                          <Radio value="keep">Keep current</Radio>
                        </RadioGroup>
                      </div>
                    );
                  })}
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                color="primary"
                onPress={handleTradingSetupResolutionConfirm}
                isLoading={isApplyingResolution}
              >
                Confirm
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );

  // Redirecting state - show minimal placeholder during navigation
  if (isRedirecting) {
    return (
      <Card
        radius="none"
        shadow="none"
        className="h-full max-h-dvh w-full flex flex-col border-0"
      >
        <CardBody className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <p className="text-text-secondary">
              Redirecting to session selection...
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Loading state - single centered skeleton card
  if (isLoading) {
    return (
      <Card
        radius="none"
        shadow="none"
        className="h-full max-h-dvh w-full flex flex-col border-0"
      >
        <CardBody className="flex-1 flex items-center justify-center p-6">
          <Card className="w-full max-w-2xl">
            <CardHeader className="pb-3 border-b-1">
              <Skeleton className="h-7 w-48" />
            </CardHeader>
            <CardBody className="pt-6">
              <div className="space-y-4">
                <Skeleton className="h-12 w-full rounded-lg" />
                <Skeleton className="h-12 w-full rounded-lg" />
                <Skeleton className="h-12 w-full rounded-lg" />
              </div>
            </CardBody>
          </Card>
        </CardBody>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card
        radius="none"
        shadow="none"
        className="h-full max-h-dvh w-full flex flex-col border-0"
      >
        <CardBody className="flex-1 flex items-center justify-center p-6">
          <EmptyState
            icon={<AlertCircle size={48} className="text-danger-500" />}
            title="Unable to Load Settings"
            description={error}
            action={
              <Button
                color="primary"
                variant="flat"
                startContent={<RefreshCw size={16} />}
                onPress={handleRetry}
              >
                Retry
              </Button>
            }
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card
      radius="none"
      shadow="none"
      className="h-full max-h-dvh w-full flex flex-col border-0"
    >
      {/* Recovery modal - rendered at top level so it overlays content */}
      {recoveryModal}

      {/* Settings Changed modal - prompts when config mismatch detected */}
      {settingsChangedModal}

      {/* Trading Setup Resolution Prompt — per-section apply/keep after defaults save (T11 Phase 3) */}
      {tradingSetupResolutionModal}

      {/* Health Metrics Config modal - for editing health metrics from workflow */}
      {draftConfig && globalSettings && draftId && (
        <HealthMetricsConfigModal
          isOpen={showHealthMetricsConfigModal}
          onClose={() => setShowHealthMetricsConfigModal(false)}
          draftId={draftId}
          draftConfig={draftConfig}
          currentSettings={globalSettings}
          onSaveSuccess={handleHealthMetricsConfigSuccess}
        />
      )}

      {/* Personal Prep Fields Config modal - for editing personal prep fields from workflow */}
      {draftConfig && globalSettings && draftId && (
        <PersonalPrepFieldsConfigModal
          isOpen={showPersonalPrepConfigModal}
          onClose={() => setShowPersonalPrepConfigModal(false)}
          draftId={draftId}
          draftConfig={draftConfig}
          currentSettings={globalSettings}
          onSaveSuccess={handlePersonalPrepConfigSuccess}
        />
      )}

      {/* Objectives Config modal - for editing objectives settings from workflow */}
      {draftConfig && globalSettings && draftId && (
        <ObjectivesWorkflowConfigModal
          isOpen={showObjectivesConfigModal}
          onClose={() => setShowObjectivesConfigModal(false)}
          draftId={draftId}
          draftConfig={draftConfig}
          currentSettings={globalSettings}
          onSaveSuccess={handleObjectivesConfigSuccess}
        />
      )}

      {/* Market Snapshot Fields Config modal - for editing snapshot field definitions from workflow */}
      {globalMarketSnapshotSettings && draftId && (
        <MarketSnapshotFieldsConfigModal
          isOpen={showMarketSnapshotFieldsConfigModal}
          onClose={() => setShowMarketSnapshotFieldsConfigModal(false)}
          draftId={draftId}
          currentFieldConfig={
            marketStatesValues[0]?.fieldConfigSnapshot
              ?? buildFieldConfigSnapshot(globalMarketSnapshotSettings.snapshotFields)
          }
          currentSettings={globalMarketSnapshotSettings}
          marketStates={marketStatesValues}
          onSaveSuccess={handleMarketSnapshotFieldsConfigSuccess}
        />
      )}

      {/* Economic Calendar Config modal - for editing calendar filters from workflow */}
      <EconomicCalendarConfigModal
        isOpen={showEconomicCalendarConfigModal}
        onClose={() => setShowEconomicCalendarConfigModal(false)}
        onSaveSuccess={handleEconomicCalendarSettingsSaved}
      />

      {/* Final Checks Config modal - for editing final check items from workflow */}
      {draftConfig && globalSettings && draftId && (
        <FinalChecksConfigModal
          isOpen={showFinalChecksConfigModal}
          onClose={() => setShowFinalChecksConfigModal(false)}
          draftId={draftId}
          draftConfig={draftConfig}
          currentSettings={globalSettings}
          onSaveSuccess={handleFinalChecksConfigSuccess}
        />
      )}

      {/* Risk Detail modal — for viewing cascade and setting session overrides */}
      {draftId && accountRiskConfig && selectedRiskProfileId && defaultAccountIdValue && (
        <RiskDetailModal
          isOpen={showRiskDetailModal}
          onClose={handleRiskDetailClose}
          onSave={handleRiskDetailSave}
          riskContext={riskDetailContext}
          accountRiskConfig={accountRiskConfig}
          selectedRiskProfileId={selectedRiskProfileId}
          defaultAccountId={defaultAccountIdValue}
          sessionStrategyOverrides={sessionStrategyOverrides}
          sessionPlaybookOverrides={sessionPlaybookOverrides}
          sessionDraftId={draftId}
          computeConfirmationState={computeConfirmationState}
        />
      )}

      {/* Trading Setup Config modal (T11) — for editing global trading setup defaults from workflow */}
      {globalTradingSetupSettings && (
        <TradingSetupConfigModal
          isOpen={showTradingSetupConfigModal}
          onClose={() => setShowTradingSetupConfigModal(false)}
          onSave={handleTradingSetupConfigSave}
          initialPairs={globalTradingSetupSettings.defaultStrategyPlaybookPairs}
          initialAccountId={globalTradingSetupSettings.defaultAccountId}
          initialFocusInstrumentIds={globalTradingSetupSettings.defaultFocusInstrumentIds}
        />
      )}

      {/* Trading Setup Incomplete blocking modal */}
      <Modal
        isOpen={showSetupIncompleteModal}
        isDismissable={false}
        hideCloseButton={true}
        backdrop="blur"
        size="md"
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader>
                <span className="text-lg font-semibold">Trading Setup Incomplete</span>
              </ModalHeader>
              <ModalBody>
                <div className="py-2 space-y-4">
                  <p className="text-text-secondary">
                    Complete your Trading Setup before starting live trading:
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    {setupCompletionStatus?.missingCriteria.map((criterion) => (
                      <li key={criterion} className="text-text-secondary">
                        {tradingSetupCriterionLabels[criterion]}
                      </li>
                    ))}
                  </ul>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="primary"
                  onPress={handleSetupIncompleteGoToSetup}
                >
                  Go to Trading Setup
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* No Objective Selected warning modal */}
      <Modal
        isOpen={showNoObjectiveModal}
        onClose={() => setShowNoObjectiveModal(false)}
        size="sm"
        placement="center"
      >
        <ModalContent>
          <ModalHeader>No Objective Selected</ModalHeader>
          <ModalBody>
            <p className="text-text-secondary">
              You haven&apos;t selected an objective for today. Would you like to set one before
              starting live trading?
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={handleConfirmNoObjective}>
              Continue Anyway
            </Button>
            <Button color="primary" onPress={handleNoObjectiveGoToObjectives}>
              Go to Objectives
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Transition Screen Modal */}
      {draftId && globalSettings && (
        <TransitionScreenModal
          isOpen={showTransitionScreenModal}
          onClose={handleTransitionComplete}
          transitionDuration={globalSettings.transitionDuration}
          autoAdvance={globalSettings.autoAdvance}
          transitionQuotes={globalSettings.transitionQuotes}
          draftId={draftId}
        />
      )}

      {/* Progress Navigation */}
      <RitualProgressNav
        sections={navSections}
        currentSectionId={currentSectionId}
        onNavigate={handleNavigate}
      />

      <CardBody className="flex-1 p-0">
        <div
          ref={scrollContainerRef}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          className={`h-full overflow-y-auto px-6 scroll-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset ${scrollbarClasses}`}
          style={{
            scrollSnapType: 'y mandatory',
            scrollBehavior: 'smooth',
          }}
        >
          {/* Card 0: Welcome - responsive height for mobile (header hidden) vs desktop */}
          <div
            data-section-id="welcome"
            className={`min-h-[calc(100dvh-160px)] md:min-h-[calc(100dvh-280px)] flex items-center justify-center ${getRevealClasses('welcome')}`}
            style={{ scrollSnapAlign: 'center' }}
          >
            <Card className="w-full max-w-2xl">
              <CardBody className="py-12 text-center">
                <p className="text-sm text-text-tertiary mb-2">
                  {new Date().toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    timeZone: displayTimezone,
                  })}
                </p>
                <h2 className="text-2xl font-bold text-text-primary mb-4">
                  Pre-Trading Ritual
                </h2>
                <p className="text-text-secondary mb-8">
                  Let's prepare for today's trading session.
                </p>
                <div className="flex flex-col items-center text-text-tertiary">
                  <span className="text-sm">Scroll to begin</span>
                  <ChevronDown size={20} className="animate-bounce mt-1" />
                </div>
              </CardBody>
            </Card>
          </div>

          {/* Section Cards - rendered in order from visibleSections */}
          {visibleSections.map((section) => {
            const meta = WORKFLOW_SECTION_META[section.id];

            // Health Metrics section
            if (section.id === 'healthMetrics') {
              return (
                <div
                  key={section.id}
                  data-section-id={section.id}
                  className={`min-h-[calc(100vh-120px)] flex items-center justify-center ${getRevealClasses(section.id)}`}
                  style={{ scrollSnapAlign: 'center' }}
                >
                  <Card className="w-full max-w-2xl">
                    <CardHeader className="pb-3 border-b-1">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="text-primary-600">{meta.icon}</div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-text-primary">
                            {meta.title}
                          </h3>
                          <p className="text-sm text-text-secondary">
                            {meta.description}
                          </p>
                        </div>
                        {(draftConfig?.healthMetrics.metrics.length ?? 0) > 0 && (
                          <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            onPress={handleOpenHealthMetricsConfig}
                            aria-label="Configure health metrics"
                          >
                            <Settings size={18} />
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardBody className="pt-6">
                      {(draftConfig?.healthMetrics.metrics.length ?? 0) > 0 ? (
                        <HealthMetricsInput
                          metrics={draftConfig?.healthMetrics.metrics ?? []}
                          values={healthMetricsValues}
                          onValuesChange={handleHealthMetricsChange}
                        />
                      ) : (
                        <div className="text-center py-4">
                          <EmptyState
                            title="No health metrics configured"
                            description="Configure health metrics to track your daily wellness"
                          />
                          <Button
                            color="primary"
                            variant="flat"
                            className="mt-4"
                            onPress={handleOpenHealthMetricsConfig}
                          >
                            Configure
                          </Button>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </div>
              );
            }

            // Personal Prep section
            if (section.id === 'personalPrep') {
              return (
                <div
                  key={section.id}
                  data-section-id={section.id}
                  className={`min-h-[calc(100vh-120px)] flex items-center justify-center ${getRevealClasses(section.id)}`}
                  style={{ scrollSnapAlign: 'center' }}
                >
                  <Card className="w-full max-w-2xl">
                    <CardHeader className="pb-3 border-b-1">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="text-primary-600">{meta.icon}</div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-text-primary">
                            {meta.title}
                          </h3>
                          <p className="text-sm text-text-secondary">
                            {meta.description}
                          </p>
                        </div>
                        {(draftConfig?.personalPrepFields.length ?? 0) > 0 && (
                          <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            onPress={handleOpenPersonalPrepConfig}
                            aria-label="Configure personal prep fields"
                          >
                            <Settings size={18} />
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardBody className="pt-6">
                      {(draftConfig?.personalPrepFields.length ?? 0) > 0 ? (
                        <PersonalPrepFieldsInput
                          fields={draftConfig?.personalPrepFields ?? []}
                          values={personalPrepValues}
                          onValuesChange={handlePersonalPrepChange}
                        />
                      ) : (
                        <div className="text-center py-4">
                          <EmptyState
                            title="No personal prep fields configured"
                            description="Configure personal prep fields to track your daily routine"
                          />
                          <Button
                            color="primary"
                            variant="flat"
                            className="mt-4"
                            onPress={handleOpenPersonalPrepConfig}
                          >
                            Configure
                          </Button>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </div>
              );
            }

            // Objectives section
            if (section.id === 'objectives') {
              return (
                <div
                  key={section.id}
                  data-section-id={section.id}
                  className={`min-h-[calc(100vh-120px)] flex items-center justify-center ${getRevealClasses(section.id)}`}
                  style={{ scrollSnapAlign: 'center' }}
                >
                  <Card className="w-full max-w-2xl">
                    <CardHeader className="pb-3 border-b-1">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="text-primary-600">{meta.icon}</div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-text-primary">
                            {meta.title}
                          </h3>
                          <p className="text-sm text-text-secondary">
                            {meta.description}
                          </p>
                        </div>
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          onPress={handleOpenObjectivesConfig}
                          aria-label="Configure objectives"
                        >
                          <Settings size={18} />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardBody className="pt-6">
                      <ObjectivesSelectionStep
                        maxObjectives={draftConfig?.maxObjectives ?? 3}
                        objectives={objectivesValues}
                        onObjectivesChange={handleObjectivesChange}
                      />
                    </CardBody>
                  </Card>
                </div>
              );
            }

            // Pre-Market Analysis (Market Snapshots) section
            if (section.id === 'preMarketAnalysis') {
              return (
                <div
                  key={section.id}
                  data-section-id={section.id}
                  className={`min-h-[calc(100vh-120px)] flex items-center justify-center ${getRevealClasses(section.id)}`}
                  style={{ scrollSnapAlign: 'center' }}
                >
                  <Card className="w-full max-w-2xl">
                    <CardHeader className="pb-3 border-b-1">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="text-primary-600">{meta.icon}</div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-text-primary">
                            {meta.title}
                          </h3>
                          <p className="text-sm text-text-secondary">
                            {meta.description}
                          </p>
                        </div>
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          onPress={handleOpenMarketSnapshotFieldsConfig}
                          aria-label="Configure snapshot fields"
                        >
                          <Settings size={18} />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardBody className="pt-6">
                      <MarketSnapshotsInput
                        marketStates={marketStatesValues}
                        onMarketStatesChange={handleMarketStatesChange}
                      />
                    </CardBody>
                  </Card>
                </div>
              );
            }

            // Economic Data (Economic Calendar) section
            if (section.id === 'economicData') {
              return (
                <div
                  key={section.id}
                  data-section-id={section.id}
                  className={`min-h-[calc(100vh-120px)] flex items-center justify-center ${getRevealClasses(section.id)}`}
                  style={{ scrollSnapAlign: 'center' }}
                >
                  <Card className="w-full max-w-2xl">
                    <CardHeader className="pb-3 border-b-1">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="text-primary-600">{meta.icon}</div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-text-primary">
                            {meta.title}
                          </h3>
                          <p className="text-sm text-text-secondary">
                            {meta.description}
                          </p>
                        </div>
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          onPress={handleOpenEconomicCalendarConfig}
                          aria-label="Configure economic calendar"
                        >
                          <Settings size={18} />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardBody className="pt-6">
                      <EconomicCalendarStep
                        trackedEvents={economicEventsValues}
                        onChange={handleEconomicEventsChange}
                        displayTimezone={displayTimezone}
                        timeFormat={timeFormat}
                        sessionDate={sessionDate}
                        settingsRefreshKey={economicCalendarSettingsKey}
                      />
                    </CardBody>
                  </Card>
                </div>
              );
            }

            // Trading Setup section (WU7C T07)
            if (section.id === 'tradingSetup') {
              return (
                <div
                  key={section.id}
                  data-section-id={section.id}
                  className={`min-h-[calc(100vh-120px)] flex items-center justify-center ${getRevealClasses(section.id)}`}
                  style={{ scrollSnapAlign: 'center' }}
                >
                  <Card className="w-full max-w-2xl">
                    <CardHeader className="pb-3 border-b-1">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="text-primary-600">{meta.icon}</div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-text-primary">
                            {meta.title}
                          </h3>
                          <p className="text-sm text-text-secondary">
                            {meta.description}
                          </p>
                        </div>
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          onPress={handleOpenTradingSetupConfig}
                          aria-label="Configure trading setup defaults"
                        >
                          <Settings size={18} />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardBody className="pt-6">
                      <TradingSetupStep
                        activePlaybookSelections={activePlaybookSelections}
                        defaultAccountId={defaultAccountIdValue}
                        focusInstrumentIds={focusInstrumentIdsValues}
                        selectedRiskProfileId={selectedRiskProfileId}
                        accountRiskConfig={accountRiskConfig}
                        accountRiskLoading={accountRiskLoading}
                        accountRiskLoadError={accountRiskLoadError}
                        onChange={handleTradingSetupChange}
                        onProfileChange={handleProfileChange}
                        onLoadingChange={setSetupStepLoading}
                        onLoadErrorChange={setSetupStepLoadError}
                        setupConfirmed={setupConfirmedValue}
                        sessionStrategyOverrides={sessionStrategyOverrides}
                        sessionPlaybookOverrides={sessionPlaybookOverrides}
                        onRiskTap={handleRiskTap}
                      />
                    </CardBody>
                    {!setupStepLoading && !setupStepLoadError && (
                      <CardFooter className="border-t-1 flex justify-center py-4">
                        {setupConfirmedValue ? (
                          <Chip
                            color="success"
                            variant="flat"
                            startContent={<Check size={14} />}
                          >
                            Setup Confirmed
                          </Chip>
                        ) : (
                          <Button
                            color="primary"
                            isDisabled={activePlaybookSelections.length === 0 || !defaultAccountIdValue || !selectedRiskProfileId}
                            onPress={() => handleSetupConfirmedChange(true)}
                          >
                            Confirm Setup
                          </Button>
                        )}
                      </CardFooter>
                    )}
                  </Card>
                </div>
              );
            }

            // Final Checks section
            if (section.id === 'finalChecks') {
              return (
                <div
                  key={section.id}
                  data-section-id={section.id}
                  className={`min-h-[calc(100vh-120px)] flex items-center justify-center ${getRevealClasses(section.id)}`}
                  style={{ scrollSnapAlign: 'center' }}
                >
                  <Card className="w-full max-w-2xl">
                    <CardHeader className="pb-3 border-b-1">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="text-primary-600">{meta.icon}</div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-text-primary">
                            {meta.title}
                          </h3>
                          <p className="text-sm text-text-secondary">
                            {meta.description}
                          </p>
                        </div>
                        {(draftConfig?.finalCheckItems.length ?? 0) > 0 && (
                          <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            onPress={handleOpenFinalChecksConfig}
                            aria-label="Configure final checks"
                          >
                            <Settings size={18} />
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardBody className="pt-6">
                      {(draftConfig?.finalCheckItems.length ?? 0) > 0 ? (
                        <FinalChecksStep
                          finalCheckItems={draftConfig?.finalCheckItems ?? []}
                          finalChecks={finalChecksValues}
                          onChange={handleFinalChecksChange}
                        />
                      ) : (
                        <div className="text-center py-4">
                          <EmptyState
                            title="No check items configured"
                            description="Configure final check items to track your pre-trading checklist"
                          />
                          <Button
                            color="primary"
                            variant="flat"
                            className="mt-4"
                            onPress={handleOpenFinalChecksConfig}
                          >
                            Configure
                          </Button>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </div>
              );
            }

            // Coming Soon placeholder sections (remaining unimplemented sections)
            return (
              <div
                key={section.id}
                data-section-id={section.id}
                className={`min-h-[calc(100vh-120px)] flex items-center justify-center ${getRevealClasses(section.id)}`}
                style={{ scrollSnapAlign: 'center' }}
              >
                <Card className="w-full max-w-2xl">
                  <CardHeader className="pb-3 border-b-1">
                    <div className="flex items-center gap-3">
                      <div className="text-primary-600">{meta.icon}</div>
                      <div>
                        <h3 className="text-lg font-semibold text-text-primary">
                          {meta.title}
                        </h3>
                        <p className="text-sm text-text-secondary">
                          {meta.description}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody className="pt-6">
                    <EmptyState
                      title="Coming Soon"
                      description="This section will be available in a future update"
                    />
                  </CardBody>
                </Card>
              </div>
            );
          })}

          {/* Card: Carry-Forward Review (bookend - always visible) */}
          <div
            data-section-id="carry-forward-review"
            className={`min-h-[calc(100vh-120px)] flex items-center justify-center ${getRevealClasses('carry-forward-review')}`}
            style={{ scrollSnapAlign: 'center' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 border-b-1">
                <div className="flex items-center gap-3">
                  <div className="text-primary-600">
                    {WORKFLOW_SECTION_META['carry-forward-review'].icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary">
                      {WORKFLOW_SECTION_META['carry-forward-review'].title}
                    </h3>
                    <p className="text-sm text-text-secondary">
                      {WORKFLOW_SECTION_META['carry-forward-review'].description}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardBody className="pt-6 pb-4">
                {carryForwardLoading ? (
                  <div className="flex justify-center py-8">
                    <Spinner size="md" />
                  </div>
                ) : carryForwardCandidates.length === 0 ? (
                  <EmptyState
                    title="No Carry-Forward Drafts"
                    description="No unresolved trade setups from previous sessions"
                  />
                ) : (
                  <div className="flex flex-col gap-3">
                    {carryForwardCandidates.map((draft) => {
                      const isSubmitting = carryForwardSubmitting.has(draft.rootDraftId);
                      const selectedAction = selectedActions[draft.id];
                      return (
                        <div
                          key={draft.id}
                          className="flex items-center justify-between gap-3 p-3 rounded-lg border border-default-200 bg-default-50"
                        >
                          <span className="text-sm font-medium text-text-primary truncate flex-1">
                            {draft.tradeName ?? draft.id}
                          </span>
                          <div className="flex items-center gap-2 shrink-0">
                            <ButtonGroup
                              options={CARRY_FORWARD_OPTIONS}
                              value={selectedAction ?? null}
                              onChange={(v) => {
                                const action = v as CarryForwardAction | null;
                                setSelectedActions((prev) =>
                                  action !== null
                                    ? { ...prev, [draft.id]: action }
                                    : Object.fromEntries(Object.entries(prev).filter(([k]) => k !== draft.id))
                                );
                              }}
                              size="sm"
                              variant="flat"
                              isDisabled={isSubmitting}
                              aria-label={`Action for ${draft.tradeName ?? draft.id}`}
                            />
                            <Button
                              size="sm"
                              color={selectedAction === 'delete' ? 'danger' : 'primary'}
                              variant="flat"
                              isDisabled={!selectedAction || isSubmitting}
                              isLoading={isSubmitting}
                              onPress={() => { void handleConfirm(draft); }}
                            >
                              Confirm
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardBody>
            </Card>
          </div>

          {/* Card: Complete / Transition (bookend - always visible) */}
          <div
            data-section-id="complete"
            className={`min-h-[calc(100vh-120px)] flex items-center justify-center ${getRevealClasses('complete')}`}
            style={{ scrollSnapAlign: 'center' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 border-b-1">
                <div className="flex items-center gap-3">
                  <div className="text-primary-600">
                    {WORKFLOW_SECTION_META.complete.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary">
                      {WORKFLOW_SECTION_META.complete.title}
                    </h3>
                    <p className="text-sm text-text-secondary">
                      {WORKFLOW_SECTION_META.complete.description}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardBody className="pt-6 pb-4">
                <p className="text-text-secondary text-center">
                  You've completed your personal preparation. When you're ready,
                  proceed to the live trading dashboard.
                </p>
              </CardBody>
              <CardFooter className="border-t-1 pt-6 pb-6">
                <Button
                  color="primary"
                  size="lg"
                  className="w-full"
                  onPress={handleProceedToLive}
                >
                  Begin Live Trading
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};
